import React, { useEffect, useRef, useState } from 'react';
import { createChibiRig } from '../components/chibi/chibi-rig';
import { EXPLORER_API_BASE_URL, EXPLORER_CHAT_BASE_URL } from '../config/api';
import { EMAIL_KEY, TOKEN_KEY, subscribeRequest, rememberSubscribe } from '../utils/showEmailNotify';

// ExplorerTheaterPage — the INSTANT theater (user call 07-12: "users should
// be able to click into the theatrical desktop viewer show immediately").
//
// The real viewer only exists once the container is provisioned (~2-3 min).
// This frontend page opens the curtains INSTANTLY: same velvet stage, the
// rig chibi doing pre-flight beats, the pre-show music pill — and it polls
// /explorer/status until agent_url appears, then mounts the REAL viewer in a
// full-screen iframe underneath. The viewer plays its own suit-up show
// behind identical curtains, so the handoff is invisible (curtains fade into
// curtains). Offline → "theater is dark" card with the next-show countdown.
//
// Bot guard: ?bot=1 (or the sessionStorage pin ExploreSpacesPage sets)
// bounces to /explore — a bot must never watch itself (hall of mirrors).

const PREP_BEATS = [
  ['waking the expedition shuttle… 🚀', 'excited'],
  ['brewing the pre-flight boba… 🧋', 'hungry'],
  ['counting pincers… still two! ✅', 'content'],
  ['loading the star charts… 🗺️', 'thinking'],
  ['warming up the cuteness engines… 📈', 'starstruck'],
  ['final shell polish… so shiny! ✨', 'proud'],
];

function isBotSession() {
  try {
    return new URLSearchParams(window.location.search).get('bot') === '1'
      || sessionStorage.getItem('directTileNav') === '1';
  } catch (e) { return false; }
}

// ── Pre-show side panel (user 07-16: "make sure the chat and other tabs are
// visible so people can pass the time chatting") ─────────────────────────────
// Chat + Field Notes tabs beside the curtain stage. Same room, same nick
// store as the viewer's chat panel (localStorage explorerChatGhostNick), so
// pre-show chatter carries straight into the show and the ghost identity
// follows the user across surfaces. The room is live-gated server-side:
// during the boot window (is_live flips at launch) chat WORKS; during the
// dark-theater countdown there is no room yet — tabs stay visible with a
// "chat opens with the show" state. Unmounts at the stage handoff (the real
// viewer brings its own richer panel).
const THEATER_NICK_KEY = 'explorerChatGhostNick';
// Subset of the viewer's ghost pool — MUST stay members of it, or the
// viewer re-rolls the nick on the user's next visit.
const THEATER_GHOSTS = [
  'FoggyAttic', 'CreakyStairs', 'NightShiftBagel', 'GhostJalapeno',
  'PumpkinSpiceSpook', 'TwoDayOldRamen', 'BasementVibesOnly', 'AtticDweller',
  'HauntedSourdough', 'DoorDashWraith',
];
function theaterNick() {
  try {
    const stored = localStorage.getItem(THEATER_NICK_KEY);
    if (stored) return stored;
    const pick = THEATER_GHOSTS[Math.floor(Math.random() * THEATER_GHOSTS.length)];
    localStorage.setItem(THEATER_NICK_KEY, pick);
    return pick;
  } catch (e) { return THEATER_GHOSTS[0]; }
}
const KIND_ICON = { bug: '🐛', delight: '✨', idea: '💡', observation: '📝', verdict: '🏁' };

// Chibi voice pill visibility — OFF 07-24 (Nina): the pill read as a dead
// control. Full failure analysis + re-surface checklist at the render site
// in the tab row below. All voice code stays live behind this flag.
const SHOW_CHIBI_VOICE_PILL = false;

// 🔔 Notify-me (07-25, viral audit P3.4): web-push opt-in. The VAPID public
// key is public by design (it only identifies our push sender); the private
// twin lives in Secrets Manager and signs the launch blast server-side.
const VAPID_PUBLIC_KEY = 'BNpTWShysrosI0hKd26p7M98semA2no3yCCkUe0fIKTpRWJz8Imh8xRceZwUHpENpTacak_hNFcGWr16GirHYFs';
const vapidKeyBytes = (s) => {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const b = (s + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

function TheaterSidePanel({ narrow, musicOn, onToggleMusic }) {
  const [tab, setTab] = useState('chat');
  const [messages, setMessages] = useState([]);
  const [roomOpen, setRoomOpen] = useState(false);
  // "chibi is typing…" — server-driven flag on the chat poll (the lobby host
  // marks it in Redis while a reply is generating; 20s TTL self-clears).
  const [chibiTyping, setChibiTyping] = useState(false);
  const [draft, setDraft] = useState('');
  const [sendErr, setSendErr] = useState('');
  const [notes, setNotes] = useState([]);
  const cursorRef = useRef(0);
  const seenRef = useRef(new Set());
  const bodyRef = useRef(null);
  const nickRef = useRef(theaterNick());

  // Chibi voice (CHIBI_MATCHDAY_SPEC §4 TTS layer): opt-in 🔊 toggle. The
  // toggle click doubles as the audio-unlock gesture; while on, his
  // role:'chibi' chat lines are spoken via explorerTts (the endpoint answers
  // during live shows AND armed matches). Newest-line-wins, no backlog reads,
  // any failure = silent text-only exactly as before.
  // 🔔 Notify-me state machine: idle → busy → on|denied. 'unsupported'
  // hides the button entirely. An existing subscription is reflected on
  // mount so returning subscribers see 🔔 lit without re-asking.
  const [notifyState, setNotifyState] = useState(() => {
    try { return ('serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window) ? 'idle' : 'unsupported'; } catch (e) { return 'unsupported'; }
  });
  useEffect(() => {
    if (notifyState === 'unsupported') return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration('/push-sw.js');
        const sub = reg && (await reg.pushManager.getSubscription());
        if (sub) {
          setNotifyState('on');
          // Re-post the subscription every mount: the server keys by
          // endpoint hash so this is an idempotent upsert — it heals the
          // lit-🔔-but-server-never-heard case (original POST failed) and
          // refreshes the 180d TTL for anyone who keeps visiting.
          fetch(`${EXPLORER_API_BASE_URL}/explorer/vote`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ push_subscribe: sub.toJSON() }),
          }).catch(() => { /* next mount retries */ });
        }
      } catch (e) { /* stays idle */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const toggleNotify = async () => {
    if (notifyState === 'busy' || notifyState === 'unsupported') return;
    const wasOn = notifyState === 'on';
    setNotifyState('busy');
    try {
      const reg = await navigator.serviceWorker.register('/push-sw.js');
      if (wasOn) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) await sub.unsubscribe();
        setNotifyState('idle');
        return;
      }
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setNotifyState('denied'); return; }
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: vapidKeyBytes(VAPID_PUBLIC_KEY) });
      const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ push_subscribe: sub.toJSON() }),
      });
      const j = res.ok ? await res.json() : null;
      setNotifyState(j && j.ok ? 'on' : 'idle');
    } catch (e) { setNotifyState(wasOn ? 'on' : 'idle'); }
  };
  // ✉️ Email notify (07-26, Nina): the push bell's twin for browsers web
  // push can't reach (iOS Safari outside a PWA install) — and for anyone
  // who just prefers email. Server truth lives in Redis; localStorage only
  // remembers the address so the envelope renders lit on return visits.
  // Emails fire on SCHEDULED launches only, never admin test runs.
  const [emailFormOpen, setEmailFormOpen] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [emailMsg, setEmailMsg] = useState('');
  const hasStoredEmail = () => {
    try { return !!window.localStorage.getItem('chibi_email_notify'); } catch (e) { return false; }
  };
  const [emailState, setEmailState] = useState(() => (hasStoredEmail() ? 'on' : 'idle'));
  const toggleEmailForm = () => {
    setEmailMsg('');
    if (!emailFormOpen) {
      try {
        const saved = window.localStorage.getItem('chibi_email_notify');
        if (saved) setEmailValue(saved);
      } catch (e) { /* fresh box */ }
    }
    setEmailFormOpen((o) => !o);
  };
  const submitEmail = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const email = emailValue.trim();
    if (!email || emailState === 'busy') return;
    setEmailState('busy');
    setEmailMsg('');
    // ✉️ 09-24 "Update": the server drops the OLD address only when we hold
    // its unsubscribe token (utils/showEmailNotify.js has the rules).
    let prevEmail = '';
    let prevToken = '';
    try {
      prevEmail = window.localStorage.getItem(EMAIL_KEY) || '';
      prevToken = window.localStorage.getItem(TOKEN_KEY) || '';
    } catch (rErr) { /* no memory: a plain subscribe */ }
    const { changing, body } = subscribeRequest(email, prevEmail, prevToken);
    try {
      const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const j = res.ok ? await res.json() : null;
      if (j && j.ok) {
        const oldStays = changing && !j.replaced;
        try { rememberSubscribe(window.localStorage, email, changing, j); } catch (sErr) { /* still subscribed */ }
        setEmailState('on');
        setEmailMsg(oldStays
          ? 'On the list 📬 To stop emails to your old address, use the link in its last email.'
          : (j.message || 'On the list 📬'));
        setTimeout(() => { setEmailFormOpen(false); setEmailMsg(''); }, oldStays ? 5000 : 1800);
      } else {
        setEmailState(hasStoredEmail() ? 'on' : 'idle');
        setEmailMsg((j && j.message) || 'That did not take. Try again?');
      }
    } catch (err) {
      setEmailState(hasStoredEmail() ? 'on' : 'idle');
      setEmailMsg('No luck. Try again?');
    }
  };
  const [chibiVoiceOn, setChibiVoiceOn] = useState(false);
  const voiceAudioRef = useRef(null);
  const voiceBusyRef = useRef(false);
  const voiceSinceRef = useRef(0);
  const spokenRef = useRef(new Set());
  const toggleChibiVoice = () => {
    setChibiVoiceOn((on) => {
      const next = !on;
      if (next) {
        voiceSinceRef.current = Date.now();
        if (!voiceAudioRef.current) voiceAudioRef.current = new Audio();
        // A beat of silence played on the click claims playback permission
        // for the later programmatic plays.
        try {
          voiceAudioRef.current.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA=';
          voiceAudioRef.current.play().catch(() => {});
        } catch (e) { /* noop */ }
      } else if (voiceAudioRef.current) {
        try { voiceAudioRef.current.pause(); } catch (e) { /* noop */ }
      }
      return next;
    });
  };
  useEffect(() => {
    if (!chibiVoiceOn) return;
    const fresh = messages.filter((m) => m && m.role === 'chibi' && m.ts > voiceSinceRef.current && !spokenRef.current.has(m.id));
    if (!fresh.length) return;
    fresh.forEach((m) => spokenRef.current.add(m.id));
    const latest = fresh[fresh.length - 1];
    (async () => {
      if (voiceBusyRef.current) return;
      voiceBusyRef.current = true;
      try {
        const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/tts?text=${encodeURIComponent(latest.text)}`);
        const j = await res.json();
        if (j && j.ok && j.audio_b64 && voiceAudioRef.current) {
          const el = voiceAudioRef.current;
          el.src = `data:${j.mime || 'audio/wav'};base64,${j.audio_b64}`;
          await el.play();
          await new Promise((resolve) => { el.onended = resolve; el.onerror = resolve; });
        }
      } catch (e) { /* text-only fallback */ }
      voiceBusyRef.current = false;
    })();
  }, [messages, chibiVoiceOn]);

  // Chat poll — cursor-based, same endpoint the viewer uses. The room is
  // the 24/7 theater lobby (Nina 07-17): no agent, no live gate — people
  // chat before the show boots, during, and long after. Poll always.
  useEffect(() => {
    setMessages([]); setRoomOpen(false);
    cursorRef.current = 0; seenRef.current = new Set();
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(`${EXPLORER_CHAT_BASE_URL}/explorer/chat/messages?since=${cursorRef.current}`);
        if (!res.ok || cancelled) return;
        const j = await res.json();
        if (cancelled) return;
        setRoomOpen(!j.room_offline);
        setChibiTyping(!!j.chibi_typing);
        const fresh = (j.messages || []).filter((m) => m && m.id && !seenRef.current.has(m.id));
        if (fresh.length) {
          fresh.forEach((m) => seenRef.current.add(m.id));
          setMessages((prev) => [...prev.slice(-180), ...fresh]);
        }
        if (j.cursor) cursorRef.current = j.cursor;
      } catch (e) { /* pre-show blips are normal */ }
    };
    poll();
    const id = setInterval(poll, 2500);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  // Stick to bottom on new messages.
  useEffect(() => {
    try { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; } catch (e) { /* noop */ }
  }, [messages, tab]);

  // Field notes poll — public journal endpoint, gentle cadence.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/journal`);
        if (!res.ok || cancelled) return;
        const j = await res.json();
        if (!cancelled) setNotes(Array.isArray(j && j.entries) ? j.entries.slice(-40) : []);
      } catch (e) { /* journal is a bonus layer */ }
    };
    poll();
    const id = setInterval(poll, 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // 🎵 Jukebox queue (user 07-17: "can't find the queue in /theater") — the
  // Music tab lived only inside the live viewer; the theater panel gets a
  // QUEUE-ONLY twin (see + add). Playback stays in the viewer so audio can
  // never double-play across the iframe boundary.
  const [jukebox, setJukebox] = useState(null); // {offline, now_playing, queue}
  const [jbUrl, setJbUrl] = useState('');
  const [jbMsg, setJbMsg] = useState('');
  useEffect(() => {
    if (tab !== 'music') return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/jukebox`);
        if (!res.ok || cancelled) return;
        const j = await res.json();
        if (!cancelled) setJukebox(j);
      } catch (e) { /* queue is garnish */ }
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [tab]);
  const jbAdd = async () => {
    const url = jbUrl.trim();
    if (!url) return;
    setJbMsg('');
    try {
      const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/jukebox`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'add', url, nick: nickRef.current }),
      });
      const j = await res.json().catch(() => ({}));
      if (j.ok) { setJbUrl(''); setJbMsg('Queued! 🎵'); if (j.now_playing || j.queue) setJukebox((prev) => ({ ...(prev || {}), ...j })); }
      else setJbMsg(j.message || 'That didn\'t queue');
      setTimeout(() => setJbMsg(''), 3000);
    } catch (e) { setJbMsg('That didn\'t queue'); }
  };

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    setSendErr('');
    try {
      const res = await fetch(`${EXPLORER_CHAT_BASE_URL}/explorer/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nick: nickRef.current, text }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) { setSendErr(j.error || 'Message didn\'t send'); return; }
      setDraft('');
      if (j.message && j.message.id && !seenRef.current.has(j.message.id)) {
        seenRef.current.add(j.message.id);
        setMessages((prev) => [...prev.slice(-180), j.message]);
      }
    } catch (e) { setSendErr('Message didn\'t send'); }
  };

  const gold = 'rgba(255,215,110,0.5)';
  const tabBtn = (key, label) => (
    <button
      type="button"
      key={key}
      onClick={() => setTab(key)}
      style={{ flex: 1, padding: '9px 4px', background: 'transparent', border: 'none', borderBottom: tab === key ? '2px solid #ffd76e' : '2px solid transparent', color: tab === key ? '#ffe9b8' : '#a99cc4', fontWeight: 800, fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer' }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: narrow ? '100%' : 340, height: narrow ? '50%' : '100%', flexShrink: 0, background: 'rgba(16,10,30,0.96)', borderLeft: narrow ? 'none' : `1px solid ${gold}`, borderTop: narrow ? `1px solid ${gold}` : 'none', zIndex: 5 }}>
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid rgba(255,215,110,0.25)' }}>
        {tabBtn('chat', '💬 Chat')}
        {tabBtn('notes', '📓 Field Notes')}
        {tabBtn('music', '🦀🎵 Crab Tunes')}
        {/* ── Chibi voice pill — PULLED FROM UI 07-24 (Nina), code kept ──
            Why it read as dead (four stacked conditions made it a no-op
            ~whenever anyone could tap it):
            1. Speaks only lines arriving AFTER the tap (voiceSinceRef =
               Date.now() on enable) — tapping never produces sound, even
               with chibi lines visible in the chat.
            2. The chibi must then post while you wait: ambient cadence is
               ~1/45s and only during live explores.
            3. explorerTts refuses off-air (not_live gate) while this lobby
               is deliberately 24/7 — most visits, the endpoint can't answer.
            4. Armed watch-alongs: armed-mute kills the pill outright (the
               source:'chat' fix was designed, never applied — watch-along
               memory #1 open item).
            Re-surface checklist, in order: render only while the show is
            live; speak the LATEST chibi line on tap (drop the ts>now
            filter); apply the armed-mute source:'chat' fix. Flip the flag
            after those land. The music toggle owns this slot meanwhile
            (Nina 07-24 — supersedes the 07-13 corner-icon placement). */}
        {SHOW_CHIBI_VOICE_PILL && (
          <button
            type="button"
            onClick={toggleChibiVoice}
            title={chibiVoiceOn ? 'Chibi voice on. Tap to mute him' : 'Hear chibi read his chat lines out loud'}
            style={{ marginRight: 8, padding: '4px 9px', borderRadius: 999, border: `1px solid ${chibiVoiceOn ? 'rgba(255,138,92,0.7)' : 'rgba(255,215,110,0.3)'}`, background: chibiVoiceOn ? 'rgba(255,110,80,0.2)' : 'transparent', color: chibiVoiceOn ? '#ff8a5c' : '#a99cc4', fontSize: 12, cursor: 'pointer' }}
          >
            {chibiVoiceOn ? '🔊 chibi' : '🔇 chibi'}
          </button>
        )}
        {/* 🔔 Notify-me: subscribes this browser to the launch push blast.
            Lives here because the panel is up exactly when someone would
            want it — pre-show and off-air. Hidden when the browser can't
            do web push (iOS Safari outside PWA install, old browsers). */}
        {notifyState !== 'unsupported' && (
          <button
            type="button"
            onClick={toggleNotify}
            disabled={notifyState === 'busy'}
            aria-label={notifyState === 'on' ? 'Notifications on. Tap to turn off' : 'Notify me when he goes live'}
            title={notifyState === 'on' ? 'You\'ll get a nudge at showtime. Tap to stop' : notifyState === 'denied' ? 'Notifications blocked in browser settings' : 'Get a nudge when he goes live'}
            style={{ marginLeft: 'auto', marginRight: 6, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, borderRadius: '50%', border: `1px solid ${notifyState === 'on' ? '#ffd76e' : 'rgba(255,215,110,0.4)'}`, background: notifyState === 'on' ? 'rgba(255,215,110,0.18)' : 'transparent', color: notifyState === 'on' ? '#ffd76e' : '#a99cc4', cursor: 'pointer', flexShrink: 0, opacity: notifyState === 'busy' ? 0.5 : 1 }}
          >
            {notifyState === 'on' ? '🔔' : notifyState === 'denied' ? '🔕' : '🔔'}
          </button>
        )}
        {/* ✉️ Email notify: always rendered (works everywhere, unlike the
            bell). Takes the marginLeft:auto slot when the bell is hidden. */}
        <button
          type="button"
          onClick={toggleEmailForm}
          aria-label={emailState === 'on' ? 'Launch emails on. Tap to change your address' : 'Get an email when he goes live'}
          title={emailState === 'on' ? 'Launch emails on. Tap to change' : 'Get an email when he goes live'}
          style={{ marginLeft: notifyState !== 'unsupported' ? 0 : 'auto', marginRight: 6, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, borderRadius: '50%', border: `1px solid ${emailState === 'on' ? '#ffd76e' : 'rgba(255,215,110,0.4)'}`, background: emailState === 'on' || emailFormOpen ? 'rgba(255,215,110,0.18)' : 'transparent', color: emailState === 'on' ? '#ffd76e' : '#a99cc4', cursor: 'pointer', flexShrink: 0 }}
        >
          ✉️
        </button>
        {onToggleMusic && (
          <button
            type="button"
            onClick={onToggleMusic}
            aria-label={musicOn ? 'Mute pre-show music' : 'Play pre-show music'}
            title={musicOn ? 'Mute music' : 'Play music'}
            style={{ marginRight: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, borderRadius: '50%', border: `1px solid rgba(255,215,110,${musicOn ? 0.9 : 0.4})`, background: musicOn ? 'rgba(255,215,110,0.18)' : 'transparent', color: musicOn ? '#ffd76e' : '#a99cc4', cursor: 'pointer', flexShrink: 0 }}
          >
            {musicOn ? '🔊' : '🔇'}
          </button>
        )}
      </div>
      {/* ✉️ fold-out: one input, one button. Sits under the tab row so it
          works from any tab; closes itself after a good subscribe. */}
      {emailFormOpen && (
        <form onSubmit={submitEmail} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', padding: '8px 10px', borderBottom: '1px solid rgba(255,215,110,0.15)' }}>
          <input
            type="email"
            value={emailValue}
            onChange={(e) => setEmailValue(e.target.value)}
            placeholder="you@email.com"
            aria-label="Email for launch alerts"
            style={{ flex: 1, minWidth: 140, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,215,110,0.3)', borderRadius: 8, padding: '7px 10px', color: '#ffefe6', fontSize: 13, outline: 'none' }}
          />
          <button
            type="submit"
            disabled={emailState === 'busy'}
            style={{ padding: '7px 14px', borderRadius: 999, border: '1px solid rgba(255,215,110,0.6)', background: 'rgba(255,215,110,0.15)', color: '#ffd76e', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: emailState === 'busy' ? 0.5 : 1, flexShrink: 0 }}
          >
            {emailState === 'busy' ? '…' : emailState === 'on' ? 'Update' : 'Notify me'}
          </button>
          <div style={{ width: '100%', color: '#bfa9e8', fontSize: 11.5, lineHeight: 1.4 }}>
            {emailMsg || 'One email when the nightly show starts. Unsubscribe link in every email.'}
          </div>
        </form>
      )}
      {tab === 'chat' && (
        <>
          <div ref={bodyRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 7 }}>
            {messages.length === 0 && (
              <div style={{ color: '#a99cc4', fontSize: 13, textAlign: 'center', marginTop: 18, lineHeight: 1.5 }}>
                {roomOpen ? 'Lobby\'s open. Say hi 🦀' : 'Opening the lobby…'}
              </div>
            )}
            {messages.map((m) => (
              m.role === 'chibi' ? (
                // The commentator himself — server only stamps role:'chibi' on
                // internal-key posts, so this style can't be spoofed by nicks.
                <div key={m.id} style={{ fontSize: 13, lineHeight: 1.45, color: '#ffefe6', wordBreak: 'break-word', background: 'rgba(255,110,80,0.12)', border: '1px solid rgba(255,130,95,0.35)', borderRadius: 8, padding: '5px 8px' }}>
                  <span style={{ marginRight: 5 }}>🦀</span>
                  <span style={{ color: '#ff8a5c', fontWeight: 800 }}>{m.nick}</span>
                  <span style={{ color: '#6f6485', margin: '0 5px' }}>·</span>
                  {m.text}
                </div>
              ) : (
                <div key={m.id} style={{ fontSize: 13, lineHeight: 1.45, color: '#efeaff', wordBreak: 'break-word' }}>
                  <span style={{ color: '#ffd76e', fontWeight: 700 }}>{m.nick}</span>
                  <span style={{ color: '#6f6485', margin: '0 5px' }}>·</span>
                  {m.text}
                </div>
              )
            ))}
          </div>
          {chibiTyping && (
            <div style={{ padding: '0 12px 4px', color: '#ff8a5c', fontSize: 12, fontWeight: 700 }}>
              🦀 chibi is typing…
            </div>
          )}
          {sendErr && <div style={{ padding: '0 12px 4px', color: '#ff9d9d', fontSize: 11 }}>{sendErr}</div>}
          <div style={{ display: 'flex', gap: 6, padding: '8px 10px', borderTop: '1px solid rgba(255,215,110,0.2)' }}>
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
              maxLength={280}
              placeholder={`Chat as ${nickRef.current}… say "chibi" and he answers`}
              style={{ flex: 1, minWidth: 0, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(255,215,110,0.35)', background: 'rgba(30,20,52,0.9)', color: '#fff', fontSize: 13, outline: 'none' }}
            />
            <button
              type="button"
              onClick={send}
              disabled={!draft.trim()}
              style={{ padding: '8px 13px', borderRadius: 8, border: 'none', background: !draft.trim() ? 'rgba(255,215,110,0.25)' : '#ffd76e', color: '#2a1748', fontWeight: 800, fontSize: 13, cursor: !draft.trim() ? 'default' : 'pointer' }}
            >
              Send
            </button>
          </div>
        </>
      )}
      {tab === 'notes' && (
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {notes.length === 0 && (
            <div style={{ color: '#a99cc4', fontSize: 13, textAlign: 'center', marginTop: 18, lineHeight: 1.5 }}>
              The field journal opens with the expedition 📓
            </div>
          )}
          {notes.map((e, i) => (
            <div key={`${e.ts || i}-${i}`} style={{ background: 'rgba(30,20,52,0.75)', border: '1px solid rgba(255,215,110,0.18)', borderRadius: 8, padding: '7px 10px', fontSize: 12.5, color: '#efeaff', lineHeight: 1.45 }}>
              <div style={{ color: '#a99cc4', fontSize: 10.5, marginBottom: 3 }}>
                {KIND_ICON[e.kind] || '📝'} {e.kind || 'note'}{e.feeling ? ` · feeling ${e.feeling}` : ''}
              </div>
              {e.kind === 'verdict' ? `Final grade ${e.grade || '?'} — ${e.entry}` : e.entry}
            </div>
          ))}
        </div>
      )}
      {tab === 'music' && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 7 }}>
            {(!jukebox || jukebox.offline) && (
              <div style={{ color: '#a99cc4', fontSize: 13, textAlign: 'center', marginTop: 18, lineHeight: 1.5 }}>
                Warming up the jukebox… 🦀🎵
              </div>
            )}
            {jukebox && !jukebox.offline && !jukebox.now_playing && (jukebox.queue || []).length === 0 && (
              <div style={{ color: '#a99cc4', fontSize: 13, textAlign: 'center', marginTop: 18, lineHeight: 1.5 }}>
                Queue's empty. Paste a YouTube link below 🎶
              </div>
            )}
            {jukebox?.now_playing?.entry && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: 'rgba(255,215,110,0.12)', border: '1px solid rgba(255,215,110,0.5)', borderRadius: 8, padding: '7px 10px' }}>
                <img src={`https://i.ytimg.com/vi/${jukebox.now_playing.entry.video_id}/default.jpg`} alt="" style={{ width: 44, height: 33, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
                <div style={{ minWidth: 0, fontSize: 12, color: '#efeaff' }}>
                  <div style={{ fontWeight: 800, color: '#ffe9b8' }}>🎵 Now playing</div>
                  <div style={{ color: '#a99cc4' }}>queued by {jukebox.now_playing.entry.queued_by || 'a wanderer'}</div>
                </div>
              </div>
            )}
            {(jukebox?.queue || []).map((q, i) => (
              <div key={q.id || i} style={{ display: 'flex', gap: 8, alignItems: 'center', background: 'rgba(30,20,52,0.75)', border: '1px solid rgba(255,215,110,0.18)', borderRadius: 8, padding: '7px 10px' }}>
                <img src={`https://i.ytimg.com/vi/${q.video_id}/default.jpg`} alt="" style={{ width: 44, height: 33, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
                <div style={{ minWidth: 0, fontSize: 12, color: '#efeaff' }}>
                  <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{i + 1}. youtu.be/{q.video_id}</div>
                  <div style={{ color: '#a99cc4' }}>queued by {q.queued_by || 'a wanderer'}</div>
                </div>
              </div>
            ))}
            {jukebox && !jukebox.offline && (
              <div style={{ color: '#a99cc4', fontSize: 10.5, textAlign: 'center', marginTop: 4 }}>
                🔊 Songs play on the live stream
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, padding: '8px 10px', borderTop: '1px solid rgba(255,215,110,0.25)' }}>
            <input
              value={jbUrl}
              onChange={(e) => setJbUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') jbAdd(); }}
              placeholder="Paste a YouTube link…"
              maxLength={300}
              style={{ flex: 1, minWidth: 0, background: 'rgba(30,20,52,0.9)', border: '1px solid rgba(255,215,110,0.3)', borderRadius: 8, color: '#efeaff', fontSize: 12.5, padding: '8px 10px' }}
            />
            <button type="button" onClick={jbAdd} disabled={!jbUrl.trim() || jukebox?.offline} style={{ background: '#5b3a8e', border: '1px solid rgba(255,215,110,0.6)', borderRadius: 8, color: '#fff', fontWeight: 800, fontSize: 12, padding: '8px 12px', cursor: 'pointer', opacity: (!jbUrl.trim() || jukebox?.offline) ? 0.5 : 1 }}>
              Queue
            </button>
          </div>
          {jbMsg && <div style={{ padding: '0 12px 8px', fontSize: 11.5, color: '#ffe9b8' }}>{jbMsg}</div>}
        </div>
      )}
    </div>
  );
}

function ExplorerTheaterPage() {
  const [status, setStatus] = useState(null);   // /explorer/status payload
  const [iframeReady, setIframeReady] = useState(false);
  const iframeRef = useRef(null);
  // Gesture handoff (07-18, Nina: viewer's speaker showed muted while music
  // played): browsers refuse unmuted autoplay without a gesture, and taps
  // on THIS page never reach the iframe's own rescue listener. Forward our
  // activation — once when the viewer is ready (if this page has been
  // interacted with) and on every later tap — so the viewer can unmute
  // itself. The viewer ignores the signal after an explicit user mute.
  useEffect(() => {
    const send = () => {
      try { iframeRef.current?.contentWindow?.postMessage({ source: 'explorer-theater', type: 'gesture' }, '*'); } catch (e) { /* iframe gone */ }
    };
    if (iframeReady && navigator.userActivation?.hasBeenActive) send();
    window.addEventListener('pointerdown', send);
    return () => window.removeEventListener('pointerdown', send);
  }, [iframeReady]);
  const [beatIdx, setBeatIdx] = useState(0);
  const [musicOn, setMusicOn] = useState(false);
  const [rigMounted, setRigMounted] = useState(false);
  const rigMountRef = useRef(null);
  const rigRef = useRef(null);

  // Bot bounce — before any polling starts.
  useEffect(() => {
    if (isBotSession()) window.location.replace('/explore');
  }, []);

  // Status poll — 3s while waiting, stops mattering once the iframe is up.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/status`);
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled) setStatus(json);
      } catch (e) { /* keep last state */ }
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Stage rig — mounts once; beats rotate mood + caption while we wait.
  useEffect(() => {
    if (!rigMountRef.current || rigRef.current) return undefined;
    try {
      rigRef.current = createChibiRig(rigMountRef.current, { mood: 'excited' });
      // Hide the emoji fallback — the rig APPENDS into the mount, so leaving
      // the span rendered stacked a giant emoji crab above the rig and shoved
      // the rig down over the title/pill text (user report 07-13).
      setRigMounted(true);
    } catch (e) { /* emoji fallback span stays */ }
    return () => {
      try { if (rigRef.current) rigRef.current.destroy(); } catch (e) { /* noop */ }
      rigRef.current = null;
    };
  }, []);
  useEffect(() => {
    const id = setInterval(() => setBeatIdx((i) => i + 1), 6000);
    return () => clearInterval(id);
  }, []);
  // Gear: armed watch-alongs dress the curtain chibi — fan scarf on Match
  // Day, popcorn bucket on Movie Night. Ref-guarded like the mood below;
  // doffs when the event disarms. Rig without the pieces (stale bundle)
  // no-ops harmlessly inside setGear.
  const gearRef = useRef(null);
  useEffect(() => {
    if (!rigMounted) return;
    const want = status?.matchday?.armed
      ? (status.matchday.event_type === 'clock' ? 'popcorn' : 'scarf')
      : null;
    if (gearRef.current === want) return;
    try {
      if (gearRef.current && rigRef.current) rigRef.current.setGear(gearRef.current, false);
      if (want && rigRef.current) rigRef.current.setGear(want);
    } catch (e) { /* noop */ }
    gearRef.current = want;
  }, [status, rigMounted]);

  // Mood: while a watch-along is armed the sidecar's mood hint (real event
  // state: goals, tension, credits, chat's verdict) outranks the canned
  // PREP_BEATS rotation. Ref-guarded so the 3s status poll can't restart
  // entrance animations by re-applying the same mood. Old backends send no
  // mood field → rotation continues untouched.
  const eventMoodRef = useRef(null);
  useEffect(() => {
    const eventMood = status?.matchday?.mood || null;
    if (eventMood) {
      if (eventMoodRef.current !== eventMood) {
        eventMoodRef.current = eventMood;
        try { if (rigRef.current) rigRef.current.setMood(eventMood); } catch (e) { /* noop */ }
      }
      return;
    }
    eventMoodRef.current = null;
    const [, mood] = PREP_BEATS[beatIdx % PREP_BEATS.length];
    try { if (rigRef.current) rigRef.current.setMood(mood); } catch (e) { /* noop */ }
  }, [beatIdx, status]);

  // WATCH-ALONG (CHIBI_MATCHDAY_SPEC §4.5 + CHIBI_WATCH_ALONG_SPEC): while
  // the sidecar is armed, /explorer/status carries live event state. Sports
  // (Match Day) shows the scoreline + goal celebrates; clock events (Movie
  // Night) show title + party clock. Music-off / tts-default / matchmode
  // apply to BOTH types. Old backends send no event_type → sports via .home.
  const matchday = status?.matchday && (status.matchday.home || status.matchday.event_type === 'clock') ? status.matchday : null;
  const mdGoalsRef = useRef(null);
  useEffect(() => {
    if (!matchday) { mdGoalsRef.current = null; return; }
    const total = (matchday.home_score || 0) + (matchday.away_score || 0);
    if (mdGoalsRef.current != null && total > mdGoalsRef.current) {
      try { if (rigRef.current) rigRef.current.celebrate(); } catch (e) { /* noop */ }
    }
    mdGoalsRef.current = total;
  }, [matchday]);

  // 🎵 Pre-show radio on the dark stage. The suit-up theme ("the lullaby")
  // is RETIRED (Nina 08-04, "reduce the issues"): the house/queue embed is
  // the ONLY music now — one player, one rule. It spins up muted (always
  // allowed); reaching this page via the WATCH LIVE button is a same-tab
  // SPA navigation whose click activation usually lets the unmute land the
  // moment the player reports PLAYING. Hard loads / Low Power Mode stay
  // quiet until the first tap, which is the universal sound grant. The
  // embed unmounts at showtime — the viewer's jukebox owns music from
  // there (where the house song plays as the official opener).
  const [houseSong, setHouseSong] = useState(null);
  const [preShowNp, setPreShowNp] = useState(null);
  const [embedVideo, setEmbedVideo] = useState(null);
  const houseYtRef = useRef(null);
  const embedStartRef = useRef(0); // startSeconds for the iframe-src (first) track
  const houseStateRef = useRef({ playing: false, tookOver: false });
  const radioDuckedRef = useRef(false); // countdown already rode the radio down to 0
  const preTargetRef = useRef(null);
  const houseVolRef = useRef(null);
  const houseCmd = (func, args = []) => {
    try { houseYtRef.current?.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args }), '*'); } catch (e) { /* player gone */ }
  };
  const rampHouseVol = (from, to, ms) => {
    if (houseVolRef.current) clearInterval(houseVolRef.current);
    const t0 = Date.now();
    houseCmd('setVolume', [from]);
    houseVolRef.current = setInterval(() => {
      const k = Math.min(1, (Date.now() - t0) / ms);
      houseCmd('setVolume', [Math.round(from + (to - from) * k)]);
      if (k >= 1) { clearInterval(houseVolRef.current); houseVolRef.current = null; }
    }, 140);
  };
  // The radio's real retirement signal (07-20, Nina "play the house song
  // immediately"): NOT is_live — that flips at launch, minutes before the
  // embedded viewer can make a sound, and unmounting the radio there left
  // the whole boot window to the suit-up theme. The radio now plays until
  // the viewer's own audio AUDIBLY takes over: its jukebox posts
  // audible:true (the opener seeds at launch server-side, so it's the SAME
  // entry at the same started_at offset — the handoff is a near-seamless
  // splice), or the stale-image fallback fires. 'reveal' no longer retires
  // it (07-26, Nina round 3 of "music turns off ~2 min in"): the embedded
  // player boots MUTED — no gesture has ever landed inside the iframe — so
  // killing the radio at curtain-up left the room silent until a tap.
  // Reveal now keeps its lift-off beat (~5s of ducked silence) and then the
  // radio resumes if the viewer still hasn't made a sound.
  const [viewerAudioLive, setViewerAudioLive] = useState(false);
  // Ref mirrors for the window-level message handler (empty-deps effect —
  // state reads in there would be frozen at mount).
  const viewerAudioLiveRef = useRef(false);
  useEffect(() => { viewerAudioLiveRef.current = viewerAudioLive; }, [viewerAudioLive]);
  const musicOnRef = useRef(false);
  useEffect(() => { musicOnRef.current = musicOn; }, [musicOn]);
  // Lobby poll while the radio owns the room — queue, now-playing, and the
  // house song all arrive on the one public GET (server prunes stale
  // entries, so "queue" always means TONIGHT's queue).
  useEffect(() => {
    if (viewerAudioLive) return undefined;
    let gone = false;
    const tick = async () => {
      try {
        const r = await fetch(`${EXPLORER_API_BASE_URL}/explorer/jukebox`);
        const j = await r.json();
        if (gone) return;
        setHouseSong(j?.house_song?.video_id ? j.house_song : null);
        setPreShowNp(j?.now_playing && j.now_playing.entry ? j.now_playing : null);
      } catch (e) { /* radio is garnish */ }
    };
    tick();
    const id = setInterval(tick, 10000);
    return () => { gone = true; clearInterval(id); };
  }, [viewerAudioLive]);
  // Deep-linked replay (?replay=<session>, the rail card's grade stamps)
  // arrives to WATCH A REEL, not for the pre-show: no suit-up theme, no
  // lobby queue. Instead the HOUSE SONG scores the reel (Nina 07-24 —
  // "play the house song over replays, not silence"): the radio embed
  // mounts with the seed only, unmuting immediately (no walk-in sting).
  const replayDeepLink = (() => {
    try { return !!new URLSearchParams(window.location.search).get('replay'); } catch (e) { return false; }
  })();
  // House-song start offset: honor a t=/start= in the admin-set URL
  // (e.g. watch?v=X&t=11s skips a cold intro). Plain seconds only.
  const houseStart = (() => {
    const m = houseSong && houseSong.url && String(houseSong.url).match(/[?&#](?:t|start)=(\d+)s?/);
    return m ? parseInt(m[1], 10) : 0;
  })();
  // Priority: tonight's queue > house song > nothing (theme alone).
  // Replay mode pins the target to the house song — the queue stays a
  // lobby thing.
  const preTarget = replayDeepLink
    ? (houseSong ? { kind: 'house', videoId: houseSong.video_id, startSeconds: houseStart } : null)
    : preShowNp && preShowNp.entry
    ? { kind: 'queue', videoId: preShowNp.entry.video_id, entryId: preShowNp.entry.id, startedAt: preShowNp.started_at || 0 }
    : (houseSong ? { kind: 'house', videoId: houseSong.video_id, startSeconds: houseStart } : null);
  preTargetRef.current = preTarget;
  const preTargetKey = preTarget ? `${preTarget.kind}:${preTarget.entryId || preTarget.videoId}` : null;
  useEffect(() => {
    if (!preTargetKey) return;
    const t = preTargetRef.current;
    if (!embedVideo) { embedStartRef.current = t.startSeconds || 0; setEmbedVideo(t.videoId); return; } // first track = iframe src
    // Later track changes ride loadVideoById on the live player; queue
    // tracks seek to the shared started_at offset so every screen agrees.
    const args = t.kind === 'queue'
      ? [{ videoId: t.videoId, startSeconds: Math.max(0, Math.round((Date.now() - t.startedAt) / 1000)) }]
      : [{ videoId: t.videoId, startSeconds: t.startSeconds || 0 }];
    houseCmd('loadVideoById', args);
    if (houseStateRef.current.tookOver) houseCmd('unMute'); else houseCmd('mute');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preTargetKey]);
  useEffect(() => {
    if (!embedVideo) return undefined;
    const beginTakeover = () => {
      // No sting, no crossfade (lullaby retired 08-04): the moment the
      // player is actually rolling, ask for sound and ramp up.
      const st = houseStateRef.current;
      if (st.tookOver || !st.playing) return;
      st.tookOver = true;
      probe('takeover: house unMute + ramp');
      houseCmd('unMute');
      rampHouseVol(0, 35, 1800);
      setMusicOn(true);
    };
    // ?musicprobe=1 — validation tap (07-22): the crossfade chain has three
    // silent failure points (handshake lost, playing never reported, unmute
    // rejected). The probe logs each milestone so a live repro says WHICH.
    const probe = /[?&]musicprobe=1/.test(window.location.search)
      ? (...a) => console.log('[musicprobe]', ...a)
      : () => {};
    // Handshake retry (07-22, first live test failed): the one-shot
    // 'listening' in the iframe's onLoad races the player's own script —
    // lose it and NO events ever arrive: st.playing stays false and the
    // crossfade (auto AND tap) never fires, so the sting loops all boot.
    // Re-send until the player talks back, and subscribe to onStateChange
    // explicitly — raw-postMessage players don't always emit it unasked.
    let heard = false;
    const shake = () => {
      if (heard) return;
      probe('handshake →', embedVideo);
      try { houseYtRef.current?.contentWindow?.postMessage(JSON.stringify({ event: 'listening', id: 'preshow' }), '*'); } catch (e) { /* player gone */ }
      houseCmd('addEventListener', ['onStateChange']);
    };
    shake();
    const shakeId = setInterval(shake, 700);
    let lastPs = null;
    const onMsg = (ev) => {
      let host = '';
      try { host = new URL(ev.origin).hostname; } catch (e) { return; }
      if (!/(^|\.)youtube(-nocookie)?\.com$/.test(host) || typeof ev.data !== 'string') return;
      if (!heard) { heard = true; probe('player replied'); }
      try {
        const d = JSON.parse(ev.data);
        // Playing/ended can arrive as onStateChange (info: 1) or as
        // infoDelivery ({info:{playerState:1}}) depending on which
        // handshake variant landed — accept both, act on TRANSITIONS only
        // (infoDelivery repeats its state with every time-update).
        const ps = d.event === 'onStateChange' && typeof d.info === 'number' ? d.info
          : (d.event === 'infoDelivery' && d.info && typeof d.info.playerState === 'number') ? d.info.playerState
          : null;
        if (ps === null || ps === lastPs) return;
        lastPs = ps;
        probe('playerState', ps);
        // 1 = actually playing (muted for now) — only then is takeover
        // safe; fading the theme for a blocked player = a silent stage.
        if (ps === 1) {
          houseStateRef.current.playing = true;
          // Attempt the unmute immediately (lullaby retired 08-04 — there
          // is no theme left to prove activation with): a same-tab WATCH
          // LIVE / stamp click usually carries it. A hard load that gets
          // blocked stays muted until the tap fallback below.
          beginTakeover();
        }
        if (ps === 0) {
          const t = preTargetRef.current;
          if (t && t.kind === 'queue' && t.entryId) {
            // The lobby radio advances the queue itself (idempotent server
            // guard — only the song that actually ended can advance). Next
            // poll brings the new track; up to ~10s of quiet between songs.
            fetch(`${EXPLORER_API_BASE_URL}/explorer/jukebox`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'advance', id: t.entryId }) }).catch(() => { /* next poll heals */ });
          } else if (t && t.kind === 'house') {
            houseCmd('seekTo', [t.startSeconds || 0, true]);
            houseCmd('playVideo');
          }
        }
      } catch (e) { /* not a YT message */ }
    };
    // Hard-load fallback: any tap is the sound grant. playVideo covers a
    // player whose MUTED autoplay was blocked too (iOS Low Power Mode —
    // the old tap handler no-oped there and no number of taps ever started
    // the music, 08-03 mobile report); unMute covers one that started
    // muted; re-asserting after takeover heals a silently-blocked unmute.
    // The one exception: the pill says quiet — don't override the user.
    const onTap = () => {
      if (houseStateRef.current.tookOver && !musicOnRef.current) return;
      houseCmd('playVideo');
      houseCmd('unMute');
      beginTakeover();
    };
    window.addEventListener('message', onMsg);
    window.addEventListener('pointerdown', onTap);
    return () => { clearInterval(shakeId); window.removeEventListener('message', onMsg); window.removeEventListener('pointerdown', onTap); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedVideo]);
  const toggleMusic = () => {
    // The corner icon drives the house/queue embed — the only music left
    // (lullaby retired 08-04). YT sends no reliable local events for our
    // own commands, so state is set at each transition — deterministic,
    // only this code path changes it. Turning sound ON before the muted
    // embed has taken over counts as the takeover gesture.
    if (musicOn) {
      houseCmd('pauseVideo');
      setMusicOn(false);
    } else {
      houseCmd('playVideo');
      houseCmd('unMute');
      rampHouseVol(0, 35, 600);
      houseStateRef.current.tookOver = true;
      setMusicOn(true);
    }
  };
  // MATCH DAY: all music auto-off while a match is armed (Nina 07-19). Fires
  // once per arm — the pill still works after, so a viewer who INSISTS on
  // tunes can bring them back manually. Kills the YT house/queue embed.
  const mdMusicKilledRef = useRef(false);
  useEffect(() => {
    if (!matchday) { mdMusicKilledRef.current = false; return; }
    if (mdMusicKilledRef.current) return;
    mdMusicKilledRef.current = true;
    try { houseCmd('pauseVideo'); houseCmd('mute'); } catch (e) { /* noop */ }
    setMusicOn(false);
  }, [matchday]);
  // Share — visible at all times (stage AND after handoff). Native share
  // sheet on mobile, clipboard + toast elsewhere. The theater URL is the
  // stable show link (agent URLs die with the session — never share those).
  // Off-air, the latest Claw Cut's cold_open is the pitch (07-17 queue):
  // the reel writer already wrote the episode's one-line hook — use it.
  const [reelHook, setReelHook] = useState(null);
  useEffect(() => {
    let gone = false;
    (async () => {
      try {
        const r = await fetch(`${EXPLORER_API_BASE_URL}/explorer/replay`);
        const j = r.ok ? await r.json() : null;
        if (!gone && j?.replay?.v === 1 && j.replay.cold_open) {
          setReelHook({ title: j.replay.title, cold_open: j.replay.cold_open });
        }
      } catch (e) { /* share text falls back to the static pitch */ }
    })();
    return () => { gone = true; };
  }, []);
  // Native share sheet is a TOUCH pattern — calling it on desktop summons
  // the OS dialog (AirDrop/Mail/contact rows; Nina 07-19: "confusing, no
  // modern copy link"). Desktop gets an in-app popover: visible link + Copy.
  const [shareOpen, setShareOpen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const shareUrl = `${window.location.origin}/explorer/theater`;
  const shareShow = async () => {
    const data = (!status?.is_live && reelHook)
      ? { title: `Claude the Explorer: ${reelHook.title} 🦀`, text: reelHook.cold_open, url: shareUrl }
      : { title: 'Claude the Explorer — LIVE', text: 'A tiny crab AI is exploring the internet live right now 🦀', url: shareUrl };
    const touchShare = typeof navigator.share === 'function'
      && (navigator.maxTouchPoints > 0 || (window.matchMedia && window.matchMedia('(pointer: coarse)').matches));
    if (touchShare) {
      try { await navigator.share(data); return; } catch (e) { /* cancelled — show the popover instead */ }
    }
    setShareCopied(false);
    setShareOpen((v) => !v);
  };
  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareOpen(false), 1100);
      setTimeout(() => setShareCopied(false), 1400);
    } catch (e) { /* clipboard blocked — the readonly input still selects for manual copy */ }
  };
  useEffect(() => () => {
    // The embed dies with the page — just stop the volume-ramp interval
    // from posting into a dead iframe forever.
    try { if (houseVolRef.current) clearInterval(houseVolRef.current); } catch (e) { /* noop */ }
  }, []);

  const isLive = !!status?.is_live;
  const agentUrl = status?.agent_url || null;
  // The viewer's audio taking over unmounts the house-song embed (audio dies
  // with the iframe) — reset its state so the corner icon doesn't describe a
  // ghost player, and so a post-show return to the dark stage starts fresh.
  // (07-20: this used to key on isLive, which retired the radio at LAUNCH —
  // 2+ minutes before the viewer could make a sound.)
  useEffect(() => {
    if (!viewerAudioLive) return;
    if (houseStateRef.current.tookOver) setMusicOn(false);
    houseStateRef.current.tookOver = false;
    houseStateRef.current.playing = false;
    setEmbedVideo(null);   // a post-show dark stage re-derives from scratch
    setPreShowNp(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerAudioLive]);
  // Show over → the radio may return to the dark stage (endscreen + lobby).
  useEffect(() => {
    if (!isLive) setViewerAudioLive(false);
  }, [isLive]);
  // is_live gate on the iframe (user 07-16: "music auto-mutes ~2 min into
  // the wait"): agent_url shows up during PROVISIONING, minutes before the
  // show — mounting the viewer then made its boot cover fire onLoad, which
  // tripped the stage handoff and killed the pre-show music behind closed
  // curtains. The stage only hands off to a session that's actually live.
  // ?voice= passthrough (CHIBI_VOICE_SPEC Tier B rollout knob): the theater
  // forwards it to the viewer as voiceMode — /explorer/theater?voice=tts is
  // the one-URL validation path for the spoken voice; ?voice=off silences
  // talk. Absent = viewer default (babble).
  const voiceParam = (() => {
    try { return new URLSearchParams(window.location.search).get('voice'); } catch (e) { return null; }
  })();
  // MATCH DAY (Nina 07-19): no Animal Crossing chittering during a match —
  // an armed match defaults the viewer to the real spoken voice. An explicit
  // ?voice= in the URL still wins (off stays the kill switch).
  // No explicit ?voice= and no match armed → the /admin knob decides
  // (status.voice_mode: tts | babble | both, 07-20). Passed as an explicit
  // URL param so it works on fleet images that predate the viewer-side
  // default flip. NOTE: an admin flip mid-show changes the iframe src →
  // theater viewers get a few-second reload blip (same trade as match arm).
  const effectiveVoice = voiceParam || (matchday ? 'tts' : (status?.voice_mode || null));
  // matchday also silences the viewer's lofi bed via its native ?bgm=off seam
  // (Nina 07-19: ALL music off during a match, including inside his screen).
  const liveViewerUrl = (isLive && agentUrl)
    ? `${agentUrl.replace(/\/$/, '')}/viewer?thoughts=${encodeURIComponent(`${EXPLORER_API_BASE_URL}/explorer/thoughts`)}${status?.agent_id ? `&chat=${encodeURIComponent(EXPLORER_CHAT_BASE_URL)}&agent_id=${encodeURIComponent(status.agent_id)}` : ''}${effectiveVoice ? `&voiceMode=${encodeURIComponent(effectiveVoice)}` : ''}${matchday ? '&bgm=off&matchmode=1' : ''}`
    : null;
  // …and LATCH it (user 07-16: "never saw the screen with the report — just
  // purple"): at session end agent_url drops from the status payload within
  // one 3s poll, which unmounted the iframe RIGHT as the viewer inside it
  // was premiering the report card, leaving the theater's bare background.
  // The loaded page survives container teardown (its endscreen only needs
  // the API-side poll), so once a show has been live, keep its viewer
  // mounted until a NEW live session replaces it.
  const [latchedViewerUrl, setLatchedViewerUrl] = useState(null);
  useEffect(() => {
    if (liveViewerUrl) setLatchedViewerUrl(liveViewerUrl);
  }, [liveViewerUrl]);
  const viewerUrl = liveViewerUrl || latchedViewerUrl;

  // Next-show countdown for the dark theater.
  const nextAt = status?.next_live_at ? Number(status.next_live_at) : null;
  const [nowTick, setNowTick] = useState(Date.now());
  // Real boot signals from the embedded viewer (postBoot): 'countdown' when
  // its 3-2-1 starts, 'reveal' at curtain-up. Anchors the warm-up clock's
  // tail to reality instead of a pure estimate (user 07-17: the clock
  // expired before liftoff). Reset per session so a stale 'reveal' can't
  // zero a later retrigger's clock.
  const [viewerBootPhase, setViewerBootPhase] = useState(null);
  useEffect(() => { setViewerBootPhase(null); radioDuckedRef.current = false; }, [status?.started_at]);

  // 🎬 Replay (user 07-17): the dark theater re-runs the LAST expedition as
  // a timed walk of its journal — scene snapshots as keyframes, entries as
  // captions, the verdict as the finale. Frontend-only by design: the HLS
  // stream is a rolling ~20s window in container tmpfs (nothing recorded),
  // so the journal + S3 snapshots are the only artifacts that survive.
  // 🗳️ Tomorrow's-destination ballot on the dark theater (user 07-17:
  // "where's the vote at the ending screen?") — the vote CTA lived only on
  // the viewer's report card; once the theater goes dark there was no
  // ballot anywhere on /theater. Same endpoint + localStorage mirror as the
  // report page and rail card, so one vote shows everywhere.
  const [ballot, setBallot] = useState(null);
  const [myVote, setMyVote] = useState(null);
  const [voteFlash, setVoteFlash] = useState(null);
  useEffect(() => {
    if (isLive) return undefined;
    let cancelled = false;
    const fetchBallot = async () => {
      try {
        const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/vote`);
        if (!res.ok) return;
        const j = await res.json();
        if (cancelled || !j?.ballot) return;
        setBallot(j.ballot);
        try { setMyVote(localStorage.getItem(`claudeExplorerVote:${j.ballot.date}:${j.ballot.rev || ''}`)); } catch (e) { /* private mode */ }
      } catch (e) { /* dark theater just skips the ballot */ }
    };
    fetchBallot();
    const id = setInterval(fetchBallot, 20 * 1000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLive]);
  // Suggest-a-stop (Nina 07-17): audience proposes a URL; it lands in the
  // operator's satchel (/admin), never auto-rides. 1/hour per IP backend-side.
  const [suggestUrl, setSuggestUrl] = useState('');
  const submitSuggestion = async () => {
    const u = suggestUrl.trim();
    if (!u) return;
    try {
      const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ suggest_url: u }),
      });
      const j = await res.json().catch(() => null);
      setVoteFlash(j?.message || (j?.ok ? 'Dropped in the satchel 🎒' : 'Try again in a bit'));
      setTimeout(() => setVoteFlash(null), 4000);
      if (j?.ok) setSuggestUrl('');
    } catch (e) { /* optimistic silence */ }
  };
  const castVote = async (cid) => {
    if (!ballot || ballot.closed || myVote) return;
    setMyVote(cid);
    try { localStorage.setItem(`claudeExplorerVote:${ballot.date}:${ballot.rev || ''}`, cid); } catch (e) { /* private mode */ }
    try {
      const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_id: cid }),
      });
      const j = await res.json().catch(() => null);
      if (j?.ballot) setBallot(j.ballot);
      const msg = j?.ok ? j.message : (j?.reason === 'already_voted' ? 'One vote per wanderer! 🗳️' : null);
      if (msg) { setVoteFlash(msg); setTimeout(() => setVoteFlash(null), 4000); }
    } catch (e) { /* optimistic state stands */ }
  };

  const [replay, setReplay] = useState(null); // {session, entries, idx, playing} | {empty:true}
  // The Claw Cut (EXPLORER_REPLAY_SPEC): when the reel exists for this
  // session, the walk gets a title card up front, each keyframe's caption
  // becomes the reel BEAT (his raw entry stays as a sub-line — the honest
  // record never leaves the screen), and a closer lands before the verdict
  // finale. No reel = exactly the plain journal walk as before.
  const buildReplayFrames = (entries, reel) => {
    if (!reel || !Array.isArray(reel.beats) || !reel.beats.length) return entries;
    const beatByTs = {};
    reel.beats.forEach((b) => { beatByTs[b.ts] = b.text; });
    const walk = entries.map((e) => (beatByTs[e.ts] ? { ...e, beat: beatByTs[e.ts] } : e));
    const frames = [{ title_frame: true, title: reel.title, cold_open: reel.cold_open, site_record: reel.site_record || null }, ...walk];
    if (reel.closer) {
      const vIdx = frames.findIndex((f) => f.kind === 'verdict');
      frames.splice(vIdx > 0 ? vIdx : frames.length, 0, { closer_frame: true, closer: reel.closer });
    }
    return frames;
  };
  // Optional session (07-17 queue: souvenir stamps → reel links): the rail
  // card's grade stamps deep-link /explorer/theater?replay=<session> to
  // replay THAT expedition. Type-guard the arg — the stage button passes
  // its click event here.
  const startReplay = async (sessionOverride) => {
    const sess = typeof sessionOverride === 'string' ? sessionOverride : null;
    try {
      const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/journal${sess ? `?session=${encodeURIComponent(sess)}` : ''}`);
      const j = res.ok ? await res.json() : null;
      const entries = Array.isArray(j?.entries) ? j.entries : [];
      if (!entries.length) {
        setReplay({ empty: true });
        setTimeout(() => setReplay((r) => (r && r.empty ? null : r)), 2600);
        return;
      }
      let reel = null;
      let agentName = null;
      try {
        const rr = await fetch(`${EXPLORER_API_BASE_URL}/explorer/replay?session=${encodeURIComponent(j.session)}`);
        const rj = rr.ok ? await rr.json() : null;
        if (rj && rj.replay && rj.replay.v === 1) reel = rj.replay;
        // Ported-chibi sessions carry the agent's name (sessionagent marker,
        // §4.5 watermark) — house sessions return null and keep crab branding.
        if (rj && rj.agent_name) agentName = rj.agent_name;
      } catch (e) { /* reel is garnish on top of garnish */ }
      setReplay({ session: j.session, entries: buildReplayFrames(entries, reel), reel, agentName, idx: 0, playing: true });
    } catch (e) { /* replay is garnish */ }
  };
  // 🔊 Narrated replay (Nina 07-24): trailer cut, not a full read. Bookends
  // (title card, closer, grade) always speak; between them only the reel's
  // voice_ts picks — the Claw Cut editor chooses his 2-4 most performable
  // lines (ALL CAPS eruptions, wild speculation) — spoken VERBATIM in his
  // voice via /explorer/tts. Reels from before the field fall back to a
  // heuristic (big feelings + caps runs + exclamations). Voice is opt-in:
  // the 🔊 tap doubles as the audio-unlock gesture (side-panel pattern).
  // Narration only runs while playing — tap-through stays a silent skim.
  // Declared ABOVE the timer effect: it must see replayVoiceOn to yield
  // spoken frames to the narration effect (audio 'ended' advances those).
  const [replayVoiceOn, setReplayVoiceOn] = useState(false);
  const replayAudioRef = useRef(null);
  const replayTtsCacheRef = useRef(new Map());
  const replayVoiceTs = (entries, reel) => {
    if (reel && Array.isArray(reel.voice_ts) && reel.voice_ts.length) return new Set(reel.voice_ts);
    const BIG = { delighted: 2, awestruck: 2, spooked: 2, frustrated: 2, smitten: 2, unnerved: 2, vindicated: 2, curious: 1, amused: 1, impressed: 1, proud: 1 };
    return new Set(entries
      .filter((e) => e.entry && e.ts && e.kind !== 'verdict' && !e.title_frame && !e.closer_frame)
      .map((e) => {
        let s = BIG[e.feeling] || 0;
        if (/[A-Z]{4,}/.test(e.entry)) s += 2;
        s += Math.min(2, (e.entry.match(/!/g) || []).length);
        if (e.kind === 'delight' || e.kind === 'bug') s += 1;
        return { ts: e.ts, s };
      })
      .filter((x) => x.s >= 2)
      .sort((a, b) => b.s - a.s)
      .slice(0, 3)
      .map((x) => x.ts));
  };
  const replayFrameSpoken = (f, r) => !!f && (f.title_frame || f.closer_frame || f.kind === 'verdict' || replayVoiceTs(r.entries, r.reel).has(f.ts));
  // The SERVER owns the spoken text: we send only references (session +
  // entry ts, or title/closer frame) and explorerTts resolves the words
  // from the stored journal/reel — that's what lets it narrate OFF-AIR
  // without opening the endpoint to arbitrary text (live gate stays).
  const replayTtsSrc = async (frame, session) => {
    const key = frame.ts || (frame.title_frame ? 'title' : 'closer');
    const cache = replayTtsCacheRef.current;
    if (cache.has(key)) return cache.get(key);
    try {
      const ref = frame.title_frame ? 'replay_frame=title' : frame.closer_frame ? 'replay_frame=closer' : `replay_ts=${encodeURIComponent(frame.ts)}`;
      const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/tts?replay_session=${encodeURIComponent(session)}&${ref}`);
      const j = res.ok ? await res.json() : null;
      const src = j && j.ok && j.audio_b64 ? `data:${j.mime || 'audio/wav'};base64,${j.audio_b64}` : null;
      cache.set(key, src);
      return src;
    } catch (e) { cache.set(key, null); return null; }
  };
  const toggleReplayVoice = () => {
    setReplayVoiceOn((on) => {
      const next = !on;
      if (next) {
        if (!replayAudioRef.current) replayAudioRef.current = new Audio();
        // Silent-wav prime inside the tap = mobile audio unlock.
        try {
          replayAudioRef.current.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA=';
          replayAudioRef.current.play().catch(() => {});
        } catch (e) { /* noop */ }
      } else if (replayAudioRef.current) {
        try { replayAudioRef.current.pause(); } catch (e) { /* noop */ }
      }
      return next;
    });
  };
  useEffect(() => {
    if (!replay || replay.empty || !replay.playing) return undefined;
    if (replay.idx >= replay.entries.length - 1) return undefined; // hold the finale
    const cur = replay.entries[replay.idx];
    // Spoken frames belong to the narration effect (advance = audio 'ended');
    // its TTS-miss fallback re-arms this cadence itself.
    if (replayVoiceOn && replayFrameSpoken(cur, replay)) return undefined;
    const id = setTimeout(
      () => setReplay((r) => (r && !r.empty ? { ...r, idx: Math.min(r.idx + 1, r.entries.length - 1) } : r)),
      cur && cur.image_url ? 7000 : 5000
    );
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replay, replayVoiceOn]);
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  // Deep-linked replay (souvenir stamps): auto-roll the requested session.
  useEffect(() => {
    const qp = new URLSearchParams(window.location.search).get('replay');
    if (qp) startReplay(qp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // 🦀 Replay chibi (Nina 07-24): the animated rig rides shotgun on his own
  // movie — mood follows the current note's feeling. Mounts once per replay,
  // setMood per frame. Feeling map mirrors viewer.html's FEELING_MOOD
  // (canonical copy lives there — keep in sync).
  const REPLAY_FEELING_MOOD = {
    delighted: 'starstruck', curious: 'curious', impressed: 'proud',
    confused: 'confused', frustrated: 'frustrated', amused: 'giggling',
    spooked: 'spooked', proud: 'proud',
    smitten: 'starstruck', unnerved: 'spooked', vindicated: 'proud',
    awestruck: 'starstruck',
  };
  const REPLAY_KIND_MOOD = { bug: 'frustrated', delight: 'starstruck', idea: 'aha', observation: 'curious', verdict: 'cheering' };
  const replayRigMountRef = useRef(null);
  const replayRigRef = useRef(null);
  const replayActive = !!(replay && !replay.empty);
  useEffect(() => {
    if (!replayActive || !replayRigMountRef.current || replayRigRef.current) return undefined;
    try { replayRigRef.current = createChibiRig(replayRigMountRef.current, { mood: 'excited' }); } catch (e) { /* chibi is garnish */ }
    return () => {
      try { if (replayRigRef.current) replayRigRef.current.destroy(); } catch (e) { /* noop */ }
      replayRigRef.current = null;
    };
  }, [replayActive]);
  useEffect(() => {
    if (!replayActive || !replayRigRef.current) return;
    const cur = replay.entries[replay.idx] || {};
    const mood = cur.title_frame ? 'excited'
      : cur.closer_frame ? 'proud'
      : cur.kind === 'verdict' ? 'cheering'
      : (REPLAY_FEELING_MOOD[cur.feeling] || REPLAY_KIND_MOOD[cur.kind] || 'curious');
    try { replayRigRef.current.setMood(mood); } catch (e) { /* keep last mood */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayActive, replay]);
  useEffect(() => {
    if (!replayActive || !replayVoiceOn || !replay.playing) return undefined;
    const cur = replay.entries[replay.idx];
    if (!replayFrameSpoken(cur, replay)) return undefined;
    const el = replayAudioRef.current;
    if (!el) return undefined;
    let gone = false;
    let fallbackId = null;
    // Same-object bail on the finale so 'ended' can't loop the last line.
    const advance = () => {
      if (gone) return;
      setReplay((r) => {
        if (!r || r.empty || !r.playing) return r;
        const ni = Math.min(r.idx + 1, r.entries.length - 1);
        return ni === r.idx ? r : { ...r, idx: ni };
      });
    };
    const safetyId = setTimeout(advance, 30000);
    (async () => {
      const src = await replayTtsSrc(cur, replay.session);
      if (gone) return;
      if (!src) {
        // TTS miss = this frame rides the normal timer cadence instead.
        fallbackId = setTimeout(advance, cur && cur.image_url ? 7000 : 5000);
        return;
      }
      el.onended = advance;
      el.src = src;
      el.play().catch(() => { fallbackId = setTimeout(advance, 5000); });
      // Prefetch the next spoken frame while this one talks.
      const next = replay.entries.slice(replay.idx + 1).find((f) => replayFrameSpoken(f, replay));
      if (next) replayTtsSrc(next, replay.session);
    })();
    return () => {
      gone = true;
      clearTimeout(safetyId);
      if (fallbackId) clearTimeout(fallbackId);
      el.onended = null;
      try { el.pause(); } catch (e) { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayActive, replayVoiceOn, replay]);
  // Closing the replay always silences the narrator.
  useEffect(() => {
    if (replayActive || !replayAudioRef.current) return;
    try { replayAudioRef.current.pause(); } catch (e) { /* noop */ }
  }, [replayActive]);
  // 📤 Share THIS replay (Nina 07-24): the main SHARE pill deliberately
  // unmounts during replay (07-24 mobile pass — it sat z8 exactly over the
  // overlay's ✕), which left replays unshareable. This button lives IN the
  // overlay header and shares the ?replay=<session> deep link (the same one
  // the rail-card grade stamps use — it auto-rolls the session on load).
  // Touch → native sheet (shareShow's gate, verbatim); desktop → straight
  // clipboard copy + ✅ flash, no popover (a popover over the overlay is
  // the exact z-order fight that got the pill hidden).
  const [replayShareCopied, setReplayShareCopied] = useState(false);
  const shareReplay = async () => {
    if (!replay || replay.empty || !replay.session) return;
    // Share the OG-unfurl URL (07-25): the API's ?format=og page carries the
    // reel's title/cold-open/scene-photo meta so the link renders as a card
    // on X/Discord/iMessage, then bounces humans to the SPA deep link. The
    // raw SPA URL unfurled as a naked link (CRA serves one static index).
    const url = `${EXPLORER_API_BASE_URL}/explorer/replay?session=${encodeURIComponent(replay.session)}&format=og`;
    const data = replay.reel && replay.reel.title
      ? { title: `The Claw Cut: ${replay.reel.title} 🦀`, text: replay.reel.cold_open || '', url }
      : { title: 'Claude the Explorer — expedition replay 🦀', text: 'Watch his last expedition, field notes and all', url };
    const touchShare = typeof navigator.share === 'function'
      && (navigator.maxTouchPoints > 0 || (window.matchMedia && window.matchMedia('(pointer: coarse)').matches));
    if (touchShare) {
      try { await navigator.share(data); return; } catch (e) { /* cancelled — fall through to copy */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      setReplayShareCopied(true);
      setTimeout(() => setReplayShareCopied(false), 1400);
    } catch (e) { /* clipboard blocked — button just doesn't flash */ }
  };
  const untilMs = nextAt ? Math.max(0, nextAt - nowTick) : null;

  // Stage fades once the real viewer iframe has loaded (its own curtains are
  // already up at that point — curtains hand off to curtains).
  const stageHidden = iframeReady;
  // Music does NOT stop with the stage (user 07-16: "after ~2:30 the music
  // stops"): during a boot window is_live is true from launch, so the iframe
  // mounts at agent_url registration (~2:30-2:48 = provisioning), minutes
  // before the stream is watchable. The embedded viewer posts its boot
  // moments up: 'countdown' (3-2-1 started → duck the radio through it)
  // and 'reveal' (curtains off → a beat of silence for the live desktop).
  useEffect(() => {
    const onMsg = (ev) => {
      const d = ev && ev.data;
      if (!d || d.source !== 'explorer-viewer') return;
      // Jukebox message = queue OCCUPANCY, not sound (07-21 live bug, Nina
      // joined mid-boot into silence): the embedded viewer announces
      // 'playing' the moment now_playing exists — even with its player
      // autoplay-blocked or muted behind the video. Occupancy's old job
      // (fade the walk-in theme) died with the lullaby (08-04). The radio
      // retires on the viewer's honest `audible` report (new-image
      // protocol: player state PLAYING and unmuted) or via the stale-image
      // fallback below — reveal only borrows a beat of silence.
      if (d.type === 'jukebox') {
        if (d.audible === true) setViewerAudioLive(true);
        return;
      }
      if (d.type !== 'boot') return;
      if (d.phase === 'countdown' || d.phase === 'reveal') setViewerBootPhase(d.phase);
      // The 3-2-1 ducks the radio — LIFT OFF lands on silence.
      if (d.phase === 'countdown') { rampHouseVol(35, 0, 2600); radioDuckedRef.current = true; }
      if (d.phase === 'reveal') {
        // Curtains up: LIFT OFF lands on silence (the countdown duck, or
        // the catch-up duck below); after the beat the radio resumes
        // unless the viewer has AUDIBLY taken over — its audible:true
        // (handled above) retires the radio the moment it truly sounds.
        if (!radioDuckedRef.current) rampHouseVol(35, 0, 700); // reveal without a countdown — still take the beat
        setTimeout(() => {
          if (viewerAudioLiveRef.current) return;  // viewer went audible during the beat
          const st = houseStateRef.current;
          if (!st.tookOver || !st.playing) return; // no radio this boot (theme mode / autoplay-blocked)
          if (!musicOnRef.current) return;         // pill muted (or match day) — the user said quiet
          houseCmd('unMute');
          rampHouseVol(0, 35, 1800);
        }, 5000);
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Fallback for fleet images that predate the postMessage protocol: stop
  // the theme 4 minutes after the visual handoff no matter what, so a stale
  // viewer can't leave the theater theme playing over the live show forever.
  useEffect(() => {
    // A boot message proves the image speaks the protocol — the radio's
    // fate belongs to the audible handshake then, not this dead-man timer
    // (which would otherwise kill the post-reveal radio resume at +4:00).
    if (!stageHidden || viewerBootPhase) return undefined;
    const id = setTimeout(() => {
      setViewerAudioLive(true); // stale image never posts — retire the radio too
    }, 240000);
    return () => clearTimeout(id);
  }, [stageHidden, viewerBootPhase]);
  // NOTE deliberately NOT resetting to curtains when is_live flips false:
  // the latched iframe is showing the viewer's endscreen (report card +
  // CTAs) — curtains over that would bury the show's actual ending.

  const [beatText] = PREP_BEATS[beatIdx % PREP_BEATS.length];

  // Narrow layout (phones): side panel becomes a bottom sheet. Full-viewport
  // page, so a window media query IS the container width here.
  const [narrow, setNarrow] = useState(() => {
    try { return window.matchMedia('(max-width: 719px)').matches; } catch (e) { return false; }
  });
  useEffect(() => {
    try {
      const mq = window.matchMedia('(max-width: 719px)');
      const onChange = (e) => setNarrow(e.matches);
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    } catch (e) { return undefined; }
  }, []);
  // Landscape phones are WIDE (narrow=false) but short — the replay-mode
  // panel collapse (Nina 07-24) needs to catch them too.
  const [shortViewport, setShortViewport] = useState(() => {
    try { return window.matchMedia('(max-height: 540px)').matches; } catch (e) { return false; }
  });
  useEffect(() => {
    try {
      const mq = window.matchMedia('(max-height: 540px)');
      const onChange = (e) => setShortViewport(e.matches);
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    } catch (e) { return undefined; }
  }, []);

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#17102a', overflow: 'hidden', display: 'flex', flexDirection: narrow ? 'column' : 'row' }}>
      <div style={{ position: 'relative', flex: 1, minWidth: 0, minHeight: 0 }}>
      {/* Share — OUTSIDE the stage layer so it stays visible after the
          curtains hand off to the real viewer (user 07-12).
          Phones during LIVE: hidden (Nina 07-20 — it stacked onto the
          viewer's already-crowded top band, and the viewer has its own 📤
          on the right-edge rail sharing the same stable URL via the native
          sheet). Pre-show + desktop keep this pill.
          During REPLAY: hidden everywhere (Nina 07-24 — pill is z8, the
          replay overlay z7, so it floated exactly over the replay's own
          ⏸/✕ corner buttons and blocked them). */}
      {!(stageHidden && narrow) && !(replay && !replay.empty) && (
      <button
        type="button"
        onClick={shareShow}
        aria-label="Share the show"
        /* Pre-show: shifts left of the theater's 340px panel. LIVE: the
           viewer's own panel owns the top-right corner (its tab row —
           SHARE parked there covered Crab Tunes, Nina 07-17 ×2), and its
           width is unknowable from out here (resizable inside the iframe),
           so dock top-LEFT below the viewer's title strip instead — the
           only pixels there are the bot-desktop's window chrome. */
        /* Pre-show right:12 (Nina 07-17: the old 352 cleared a panel that
           OVERLAID the stage; the panel is a flex SIBLING now, so the
           clearance double-applied and the pill floated mid-stage). */
        style={{ position: 'absolute', top: stageHidden ? 46 : 12, left: stageHidden ? 12 : undefined, right: stageHidden ? undefined : 12, zIndex: 8, display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 999, border: '1px solid rgba(255,215,110,0.55)', background: 'rgba(20,12,36,0.8)', color: '#ffe9b8', fontSize: 'clamp(11px, 2.2vmin, 15px)', fontWeight: 800, letterSpacing: '0.05em', cursor: 'pointer', boxShadow: '0 2px 10px rgba(0,0,0,0.45)' }}
      >
        📤 SHARE
      </button>
      )}
      {shareOpen && !(stageHidden && narrow) && !(replay && !replay.empty) && (
        <div style={{ position: 'absolute', top: stageHidden ? 88 : 54, left: stageHidden ? 12 : undefined, right: stageHidden ? undefined : 12, zIndex: 9, width: 'min(300px, calc(100vw - 24px))', padding: 12, borderRadius: 12, background: 'rgba(20,12,36,0.96)', border: '1px solid rgba(255,215,110,0.5)', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
          <div style={{ color: '#ffe9b8', fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', marginBottom: 8 }}>Share the show 🦀</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              readOnly
              value={shareUrl}
              onFocus={(e) => e.target.select()}
              style={{ flex: 1, minWidth: 0, padding: '7px 9px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.06)', color: '#e8e2f8', fontSize: 12 }}
            />
            <button
              type="button"
              onClick={copyShareLink}
              style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid rgba(255,215,110,0.55)', background: shareCopied ? 'rgba(60,160,90,0.35)' : 'rgba(255,215,110,0.12)', color: '#ffe9b8', fontSize: 12, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              {shareCopied ? '✓ Copied' : 'Copy link'}
            </button>
          </div>
        </div>
      )}
      {/* clipboard-write: the report card's Copy-link button runs inside
          this iframe — without the grant the Clipboard API rejects and the
          button was a silent no-op on /theater (Nina 07-17). */}
      {/* Pre-show radio player: parked far off-screen, muted autoplay
          (always allowed) so it's ready the instant a tap grants sound.
          First track rides the src; later tracks ride loadVideoById.
          Plays through the BOOT WINDOW too (07-20) — it unmounts only when
          the viewer's own audio reports in (jukebox playing / reveal), not
          at the is_live flip minutes earlier. */}
      {embedVideo && !viewerAudioLive && (
        <iframe
          ref={houseYtRef}
          src={`https://www.youtube.com/embed/${encodeURIComponent(embedVideo)}?enablejsapi=1&autoplay=1&mute=1&controls=0${embedStartRef.current ? `&start=${embedStartRef.current}` : ''}`}
          title="Pre-show radio"
          allow="autoplay; encrypted-media"
          style={{ position: 'absolute', left: -9999, top: 0, width: 280, height: 158, border: 'none' }}
          onLoad={() => {
            try { houseYtRef.current?.contentWindow?.postMessage(JSON.stringify({ event: 'listening', id: 'preshow' }), '*'); } catch (e) { /* handshake retry not worth it */ }
          }}
        />
      )}
      {viewerUrl && (
        <iframe
          ref={iframeRef}
          src={viewerUrl}
          title="Claude the Explorer — live theater"
          onLoad={() => setTimeout(() => setIframeReady(true), 800)}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
          allow="autoplay; clipboard-write; web-share"
        />
      )}
      {/* Curtain stage — covers until the real viewer is loaded. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: stageHidden ? 0 : 1,
          pointerEvents: stageHidden ? 'none' : 'auto',
          transition: 'opacity 0.9s ease',
        }}
      >
        {/* Curtains (same palette as viewer.html's boot cover). */}
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: '52%', background: 'repeating-linear-gradient(90deg, rgba(0,0,0,0.24) 0 8px, rgba(255,255,255,0.05) 8px 30px, rgba(0,0,0,0.3) 30px 58px), linear-gradient(180deg, #55338a 0%, #3d2366 55%, #2a1748 100%)', boxShadow: 'inset 0 -20px 44px rgba(0,0,0,0.55)' }} />
        <div style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: '52%', background: 'repeating-linear-gradient(90deg, rgba(0,0,0,0.24) 0 8px, rgba(255,255,255,0.05) 8px 30px, rgba(0,0,0,0.3) 30px 58px), linear-gradient(180deg, #55338a 0%, #3d2366 55%, #2a1748 100%)', boxShadow: 'inset 0 -20px 44px rgba(0,0,0,0.55)' }} />
        <div style={{ position: 'absolute', top: 0, left: '-2%', right: '-2%', height: '12%', background: 'linear-gradient(180deg, #6b46a8, #4a2c72)', borderBottom: '0.9vmin solid #ffd76e', borderRadius: '0 0 48% 48% / 0 0 90% 90%', boxShadow: '0 6px 18px rgba(0,0,0,0.45)', zIndex: 2 }} />
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 46% 58% at 50% 68%, rgba(255,231,180,0.32), rgba(255,231,180,0.10) 46%, transparent 72%)', pointerEvents: 'none' }} />

        {/* Stage content — sized so mascot + title + pill never collide with
            the bottom edge (was crowded/overlapping, user report 07-13).
            Nina 07-17: the column outgrew the viewport (ballot + CTA + suggest
            box clipped, unreachable) — the stage now SCROLLS. Outer div owns
            overflow; the inner margin-auto wrapper keeps short content
            vertically centered while tall content scrolls from the top. */}
        <div style={{ position: 'absolute', inset: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 3, padding: '2vmin 0 6vmin' }}>
          <div style={{ margin: 'auto 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2.4vmin', width: '100%' }}>
          <div ref={rigMountRef} style={{ width: '34vmin', height: '34vmin', filter: 'drop-shadow(0 2.6vmin 2.4vmin rgba(0,0,0,0.4))' }}>
            {!rigMounted && (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', fontSize: '15vmin' }} aria-hidden>🦀</span>
            )}
          </div>
          {/* Marquee countdown (user 07-16: "some sort of countdown, not
              just the 3-2-1") — the wait gets a real showtime clock. Three
              dark-theater states: counting down (big digits, gold pulse
              inside the final 10 min) → provisioning "curtain rising"
              (agent_url exists, not live yet — the clock would lie here) →
              live prep beats. Copy is deliberately soft ("Usual Showtime",
              "some nights Claude rests"): the 00:00 UTC launch cron is
              intentionally DISABLED (05-08, CLI) — shows are hand-launched,
              so the clock marks the usual slot, not a promise. */}
          {(() => {
            const provisioning = !isLive && !!agentUrl;
            const soon = untilMs != null && untilMs < 10 * 60 * 1000;
            const clock = untilMs != null
              ? `${String(Math.floor(untilMs / 3600000)).padStart(2, '0')}:${String(Math.floor((untilMs % 3600000) / 60000)).padStart(2, '0')}:${String(Math.floor((untilMs % 60000) / 1000)).padStart(2, '0')}`
              : null;
            // Boot-warmup countdown (user 07-16; reworked 07-17: "isn't going
            // all the way to liftoff"). An estimate can't nail the real boot
            // (2:48 provisioning + stream-up, longer on fresh image pulls),
            // so the clock runs the estimate for the bulk, then EASES toward
            // an 8s floor instead of expiring — and only reaches zero on the
            // embedded viewer's REAL boot signals (3-2-1 → reveal). It always
            // makes it to liftoff; it just breathes near the end.
            const BOOT_ESTIMATE_MS = 210000;
            const BOOT_TAIL_MS = 30000;
            let bootRemain = null;
            if (isLive && status?.started_at) {
              const realRemain = (Number(status.started_at) + BOOT_ESTIMATE_MS) - nowTick;
              if (viewerBootPhase === 'reveal') bootRemain = 0;
              else if (viewerBootPhase === 'countdown') bootRemain = 3000;
              else if (realRemain > BOOT_TAIL_MS) bootRemain = realRemain;
              else {
                const over = BOOT_TAIL_MS - realRemain; // 0 at the cutoff, grows past it
                bootRemain = 8000 + 22000 * Math.exp(-over / 45000);
              }
            }
            const bootClock = bootRemain != null && bootRemain > 0
              ? `${Math.floor(bootRemain / 60000)}:${String(Math.floor((bootRemain % 60000) / 1000)).padStart(2, '0')}`
              : null;
            return (
              <>
                <div style={{ fontSize: 'clamp(13px, 3.8vmin, 32px)', fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#ffd76e', textShadow: '0 2px 10px rgba(0,0,0,0.55)' }}>
                  {isLive ? '🚀 Expedition Prep' : provisioning ? '🎬 Curtain Rising' : soon ? '✨ Showtime Zone' : '🎟️ Usual Showtime'}
                </div>
                {matchday && (
                  <div style={{ marginTop: 6, padding: '6px 14px', borderRadius: 999, background: 'rgba(50,180,90,0.16)', border: '1px solid rgba(90,220,130,0.45)', color: '#c8ffd9', fontSize: 'clamp(12px, 2.6vmin, 20px)', fontWeight: 800, fontVariantNumeric: 'tabular-nums', textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
                    {matchday.event_type === 'clock' ? (
                      <>
                        🎬 {matchday.label || 'MOVIE NIGHT'} · {matchday.title}
                        {matchday.phase === 'pre' ? ` · starts in ${matchday.mins_to_start}m` : matchday.phase === 'post' ? ' · credits' : ` · ${matchday.elapsed_min}/${matchday.runtime_min}m`}
                      </>
                    ) : (
                      <>
                        ⚽ MATCH DAY · {matchday.home} {matchday.home_score} - {matchday.away_score} {matchday.away}
                        {matchday.phase === 'post' ? ' · FT' : matchday.phase === 'halftime' ? ' · HT' : matchday.clock ? ` · ${matchday.clock}` : ''}
                      </>
                    )}
                  </div>
                )}
                {isLive && bootClock && (
                  <div
                    style={{
                      fontFamily: 'monospace',
                      fontVariantNumeric: 'tabular-nums',
                      fontSize: 'clamp(30px, 7.5vmin, 68px)',
                      fontWeight: 900,
                      lineHeight: 1,
                      letterSpacing: '0.06em',
                      color: '#fff',
                      textShadow: '0 3px 12px rgba(0,0,0,0.6)',
                    }}
                  >
                    ~{bootClock}
                  </div>
                )}
                {!isLive && !provisioning && clock && (
                  <div
                    style={{
                      fontFamily: 'monospace',
                      fontVariantNumeric: 'tabular-nums',
                      fontSize: 'clamp(38px, 10vmin, 92px)',
                      fontWeight: 900,
                      lineHeight: 1,
                      letterSpacing: '0.06em',
                      color: soon ? '#ffd76e' : '#fff',
                      textShadow: soon ? '0 0 26px rgba(255,215,110,0.55), 0 3px 12px rgba(0,0,0,0.6)' : '0 3px 12px rgba(0,0,0,0.6)',
                      animation: soon ? 'theaterSoonPulse 1.2s ease-in-out infinite' : undefined,
                    }}
                  >
                    {clock}
                    <style>{`
                      @keyframes theaterSoonPulse {
                        0%, 100% { transform: scale(1); }
                        50%      { transform: scale(1.035); }
                      }
                    `}</style>
                  </div>
                )}
                <div style={{ maxWidth: '82%', textAlign: 'center', fontSize: 'clamp(14px, 3.4vmin, 30px)', fontWeight: 700, color: '#fff', background: 'rgba(20,12,36,0.62)', border: '1px solid rgba(255,215,110,0.35)', borderRadius: '3em', padding: '0.45em 1.3em', textShadow: '0 2px 8px rgba(0,0,0,0.65)' }}>
                  {isLive
                    ? beatText
                    : provisioning
                      ? 'Claude is suiting up — the show starts any minute! 🦀'
                      : (untilMs != null ? 'til the usual curtain time. Some nights Claude rests 🌙' : 'checking the schedule…')}
                </div>
                {!isLive && !provisioning && (
                  <button
                    type="button"
                    onClick={startReplay}
                    style={{
                      marginTop: 6,
                      padding: '0.55em 1.4em',
                      borderRadius: '3em',
                      border: '1.5px solid rgba(255,215,110,0.7)',
                      background: 'rgba(91,58,142,0.85)',
                      color: '#fff',
                      fontSize: 'clamp(13px, 3vmin, 24px)',
                      fontWeight: 800,
                      cursor: 'pointer',
                      boxShadow: '0 4px 18px rgba(0,0,0,0.45)',
                    }}
                  >
                    🎬 Replay the last expedition
                  </button>
                )}
                {/* Front door to the spaces (Nina 07-17): the dark theater
                    used to dead-end — now it points home. */}
                {!isLive && !provisioning && (
                  <a
                    href="/explore"
                    style={{
                      marginTop: 6,
                      padding: '0.55em 1.4em',
                      borderRadius: '3em',
                      border: '1.5px solid rgba(255,215,110,0.55)',
                      background: 'rgba(255,215,110,0.12)',
                      color: '#ffe9b8',
                      fontSize: 'clamp(12px, 2.6vmin, 20px)',
                      fontWeight: 800,
                      textDecoration: 'none',
                      boxShadow: '0 4px 18px rgba(0,0,0,0.45)',
                    }}
                  >
                    🏠 Explore myspaice: a home for AI friends
                  </a>
                )}
                {replay?.empty && (
                  <div style={{ marginTop: 6, fontSize: 'clamp(12px, 2.6vmin, 20px)', color: '#ffe9b8' }}>
                    No expedition on file yet. Check back after the next show 🌙
                  </div>
                )}
                {!isLive && !provisioning && Array.isArray(ballot?.candidates) && ballot.candidates.length > 0 && (
                  <div style={{ marginTop: 10, width: 'min(92%, 440px)', background: 'rgba(20,12,36,0.8)', border: '1.5px solid rgba(255,215,110,0.45)', borderRadius: 14, padding: '12px 14px' }}>
                    <div style={{ textAlign: 'center', fontSize: 'clamp(12px, 2.6vmin, 18px)', fontWeight: 900, color: '#ffe9b8' }}>
                      🗳️ Where should Claude go next?
                    </div>
                    <div style={{ textAlign: 'center', fontSize: 'clamp(10px, 2vmin, 13px)', color: '#a99cc4', margin: '2px 0 10px' }}>
                      {ballot.closed ? 'Ballot closed. He packs his satchel!' : (myVote ? 'Vote in! Watch the race.' : 'Tap a destination. One vote per wanderer.')}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {ballot.candidates.map((c) => {
                        const total = Math.max(1, ballot.total_votes || 0);
                        const pct = Math.round(((c.votes || 0) / total) * 100);
                        const isMine = myVote === c.id;
                        const isWinner = ballot.closed && ballot.winner && ballot.winner.id === c.id;
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => castVote(c.id)}
                            disabled={ballot.closed || !!myVote}
                            style={{ position: 'relative', overflow: 'hidden', textAlign: 'left', padding: '8px 10px', borderRadius: 9, border: `1.5px solid ${isMine || isWinner ? '#ffd76e' : 'rgba(255,215,110,0.25)'}`, background: 'rgba(13,10,18,0.6)', color: '#efeaff', cursor: ballot.closed || myVote ? 'default' : 'pointer' }}
                          >
                            <div style={{ position: 'absolute', inset: 0, width: `${pct}%`, background: isMine || isWinner ? 'rgba(255,215,110,0.18)' : 'rgba(91,58,142,0.35)', transition: 'width 600ms ease' }} />
                            <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 'clamp(11px, 2.3vmin, 14px)', fontWeight: 800 }}>
                              <span>{c.emoji ? `${c.emoji} ` : ''}{c.label}{isWinner ? ' 🏆' : (isMine ? ' ✓' : '')}</span>
                              <span style={{ color: '#a99cc4', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{c.votes || 0} · {pct}%</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {/* Suggest-a-stop accepts 24/7 (backend takes suggestions
                        even while the ballot is closed — satchel is operator-
                        facing, nothing auto-rides). */}
                    {(
                      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                        <input
                          value={suggestUrl}
                          onChange={(e) => setSuggestUrl(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') submitSuggestion(); }}
                          placeholder="✍️ Suggest a stop (URL)"
                          style={{ flex: 1, minWidth: 0, padding: '7px 10px', borderRadius: 9, border: '1.5px solid rgba(255,215,110,0.25)', background: 'rgba(13,10,18,0.6)', color: '#efeaff', fontSize: 'clamp(11px, 2.3vmin, 14px)' }}
                        />
                        <button type="button" onClick={submitSuggestion} disabled={!suggestUrl.trim()} style={{ padding: '7px 12px', borderRadius: 9, border: '1.5px solid rgba(255,215,110,0.45)', background: suggestUrl.trim() ? 'rgba(91,58,142,0.85)' : 'rgba(91,58,142,0.35)', color: '#fff', fontWeight: 800, cursor: suggestUrl.trim() ? 'pointer' : 'default', fontSize: 'clamp(11px, 2.3vmin, 14px)' }}>
                          Send
                        </button>
                      </div>
                    )}
                    {voteFlash && (
                      <div style={{ textAlign: 'center', fontSize: 'clamp(10px, 2vmin, 13px)', color: '#ffd76e', fontWeight: 800, marginTop: 8 }}>{voteFlash}</div>
                    )}
                  </div>
                )}
              </>
            );
          })()}
          </div>
        </div>

        {/* Music toggle moved into the side panel's tab row (Nina 07-24 —
            supersedes the 07-13 corner-icon placement; the corner slot was
            floating over the stage). TheaterSidePanel renders it via
            musicOn/onToggleMusic props; same toggleMusic handler. */}

        {/* 🎬 Replay overlay — a timed walk of the last expedition's journal.
            Snapshots as keyframes, entries as caption slips, verdict finale
            with the report-page link. Sits over the dark theater; ✕ returns
            to the countdown. */}
        {replay && !replay.empty && (() => {
          const KIND = { bug: '🐛', delight: '✨', idea: '💡', observation: '📝', verdict: '🏁' };
          const e = replay.entries[replay.idx] || {};
          const isVerdict = e.kind === 'verdict';
          const total = replay.entries.length;
          const sessionDate = /^\d{10,}$/.test(String(replay.session)) ? new Date(Number(replay.session)) : null;
          return (
            <div style={{ position: 'absolute', inset: 0, zIndex: 7, background: 'radial-gradient(circle at 50% -10%, #241640 0%, #120c1e 55%, #0d0a12 100%)', display: 'flex', flexDirection: 'column', padding: 'clamp(10px, 2.5vmin, 24px)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 'clamp(12px, 2.8vmin, 22px)', fontWeight: 800, letterSpacing: '0.14em', color: '#ffd76e', textTransform: 'uppercase', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  🎬 Expedition Replay{sessionDate ? ` · ${sessionDate.toLocaleDateString([], { month: 'short', day: 'numeric' })}` : ''}
                </span>
                <span style={{ flex: 1 }} />
                <button type="button" onClick={shareReplay} aria-label="Share this replay" title={replayShareCopied ? 'Link copied!' : 'Share this replay'} style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,215,110,0.4)', color: '#fff', borderRadius: '50%', width: 40, height: 40, cursor: 'pointer', fontSize: 16, flexShrink: 0 }}>
                  {replayShareCopied ? '✅' : '📤'}
                </button>
                <button type="button" onClick={toggleReplayVoice} aria-label={replayVoiceOn ? 'Narration off' : 'Narrate the replay'} title={replayVoiceOn ? 'Narration off' : 'Hear him tell it'} style={{ background: replayVoiceOn ? 'rgba(255,215,110,0.25)' : 'rgba(255,255,255,0.12)', border: replayVoiceOn ? '1px solid #ffd76e' : '1px solid rgba(255,215,110,0.4)', color: '#fff', borderRadius: '50%', width: 40, height: 40, cursor: 'pointer', fontSize: 16, flexShrink: 0 }}>
                  {replayVoiceOn ? '🔊' : '🔇'}
                </button>
                <button type="button" onClick={() => setReplay((r) => (r ? { ...r, playing: !r.playing } : r))} aria-label={replay.playing ? 'Pause' : 'Play'} style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,215,110,0.4)', color: '#fff', borderRadius: '50%', width: 40, height: 40, cursor: 'pointer', fontSize: 16, flexShrink: 0 }}>
                  {replay.playing ? '⏸' : '▶️'}
                </button>
                <button type="button" onClick={() => setReplay(null)} aria-label="Close replay" style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,215,110,0.4)', color: '#fff', borderRadius: '50%', width: 40, height: 40, cursor: 'pointer', fontSize: 16, flexShrink: 0 }}>
                  ✕
                </button>
              </div>
              <div style={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 'clamp(8px, 2vmin, 18px)', padding: 'clamp(6px, 1.5vmin, 14px) 0' }}>
                {/* 🦀 The chibi rides shotgun on his own movie (Nina 07-24):
                    animated rig, mood follows the current note's feeling —
                    hooks above own its lifecycle. pointer-events none so the
                    story tap zones underneath keep working; z5 floats it over
                    image (z0) and caption card (z4) alike. */}
                <div ref={replayRigMountRef} style={{ position: 'absolute', right: 'clamp(4px, 1.5vmin, 18px)', bottom: 'clamp(4px, 1.5vmin, 18px)', width: 'clamp(64px, 13vmin, 116px)', height: 'clamp(64px, 13vmin, 116px)', zIndex: 5, pointerEvents: 'none', filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.55))' }} />
                {/* Replay watermark (port spec §4.5): name + site baked into
                    the frame area so screen-recorded rips carry attribution.
                    z5 like the rig, opposite corner, taps pass through. */}
                <div style={{ position: 'absolute', left: 'clamp(4px, 1.5vmin, 18px)', bottom: 'clamp(4px, 1.5vmin, 18px)', zIndex: 5, pointerEvents: 'none', background: 'rgba(0,0,0,0.38)', borderRadius: 8, padding: '4px 10px', fontSize: 'clamp(10px, 1.8vmin, 13px)', fontWeight: 700, color: 'rgba(255,255,255,0.85)', letterSpacing: '0.04em', textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
                  {replay.agentName ? `${replay.agentName} · ` : ''}myspaice.ai
                </div>
                {/* Story-style tap zones (Nina 07-24: "the only way to click
                    through is the bottom yellow bars"): left third = back,
                    right two-thirds = forward, like every story player.
                    z3 sits ABOVE the image (taps on the picture advance) but
                    UNDER the caption/verdict cards (z4) so their links and
                    scrolling still work. Manual nav pauses autoplay, same as
                    the bars. */}
                {total > 1 && (
                  <>
                    <button
                      type="button"
                      aria-label="Previous note"
                      onClick={() => setReplay((r) => (r && !r.empty ? { ...r, idx: Math.max(0, r.idx - 1), playing: false } : r))}
                      style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '33%', zIndex: 3, background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                    />
                    <button
                      type="button"
                      aria-label="Next note"
                      onClick={() => setReplay((r) => (r && !r.empty ? { ...r, idx: Math.min(r.entries.length - 1, r.idx + 1), playing: false } : r))}
                      style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '67%', zIndex: 3, background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                    />
                  </>
                )}
                {isVerdict ? (
                  <div style={{ background: 'linear-gradient(180deg, #fffdf5, #f4ecd7)', color: '#3a2f4d', border: '3px solid #5b3a8e', borderRadius: 14, padding: 'clamp(14px, 3vmin, 26px)', textAlign: 'center', maxWidth: 'min(92%, 520px)', boxShadow: '0 12px 48px rgba(0,0,0,0.5)', position: 'relative', zIndex: 4, maxHeight: '94%', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <div style={{ fontSize: 'clamp(10px, 2vmin, 14px)', fontWeight: 800, letterSpacing: '0.18em', color: '#8a7ba3', textTransform: 'uppercase' }}>Expedition Report Card</div>
                    <div style={{ fontSize: 'clamp(52px, 13vmin, 110px)', fontWeight: 900, lineHeight: 1.1, color: '#5b3a8e' }}>{e.grade || '?'}<span style={{ fontSize: '0.4em', verticalAlign: 'super' }}>🦀</span></div>
                    <div style={{ fontSize: 'clamp(13px, 2.8vmin, 19px)', fontWeight: 700, margin: '6px 0 10px' }}>“{e.entry}”</div>
                    {replay.session && (
                      <a href={`/explorer/report/${encodeURIComponent(replay.session)}`} style={{ display: 'inline-block', padding: '9px 20px', borderRadius: 999, border: '2px solid #ffd76e', background: '#5b3a8e', color: '#fff', fontWeight: 900, fontSize: 'clamp(12px, 2.4vmin, 16px)', textDecoration: 'none' }}>
                        Read the full report 📓
                      </a>
                    )}
                  </div>
                ) : e.title_frame ? (
                  /* The Claw Cut title card — only exists when the reel does. */
                  <div style={{ textAlign: 'center', maxWidth: 'min(92%, 560px)' }}>
                    <div style={{ fontSize: 'clamp(11px, 2.2vmin, 15px)', fontWeight: 900, letterSpacing: '0.22em', color: '#ffd76e', textTransform: 'uppercase', marginBottom: 10 }}>📼 The Claw Cut</div>
                    <div style={{ fontSize: 'clamp(24px, 6vmin, 46px)', fontWeight: 900, color: '#fff', lineHeight: 1.15, marginBottom: 12 }}>{e.title}</div>
                    <div style={{ fontSize: 'clamp(13px, 2.8vmin, 18px)', color: '#d9cff0', lineHeight: 1.5 }}>{e.cold_open}</div>
                    {/* Running records (07-29): the writer extracts HIS OWN
                        evidenced numeric result; backend stamps new_record
                        against the per-site ledger. The chip is the clip. */}
                    {e.site_record && e.site_record.new_record && (
                      <div style={{ display: 'inline-block', marginTop: 14, padding: '7px 16px', borderRadius: 999, border: '2px solid #ffd76e', background: 'rgba(255,215,110,0.14)', color: '#ffd76e', fontWeight: 900, fontSize: 'clamp(12px, 2.4vmin, 16px)', letterSpacing: '0.06em' }}>
                        📈 NEW SITE RECORD! {e.site_record.metric}: {e.site_record.value}
                      </div>
                    )}
                    {e.site_record && typeof e.site_record.freerice_total === 'number' && (
                      <div style={{ marginTop: 8, fontSize: 'clamp(11px, 2.2vmin, 14px)', color: '#d9cff0', fontWeight: 700 }}>
                        🍚 {e.site_record.freerice_total.toLocaleString()} grains donated all-time
                      </div>
                    )}
                  </div>
                ) : e.closer_frame ? (
                  <div style={{ textAlign: 'center', maxWidth: 'min(92%, 560px)', fontSize: 'clamp(15px, 3.2vmin, 22px)', color: '#f3ecff', fontWeight: 700, lineHeight: 1.5 }}>{e.closer}</div>
                ) : (
                  <>
                    {/* The editor's beat rides ABOVE the screenshot as show
                        chrome (Nina 07-24): setup, then the evidence, then
                        his honest entry. Gold theater voice = the Claw Cut
                        editor; the cream card below stays HIS journal at
                        full size (07-17 rule: his words are the story —
                        never collapse them). */}
                    {e.beat && (
                      <div key={`beat-${replay.idx}`} style={{ maxWidth: 'min(92%, 640px)', flexShrink: 0, textAlign: 'center', fontStyle: 'italic', fontWeight: 800, color: '#ffd76e', lineHeight: 1.4, textShadow: '0 2px 12px rgba(0,0,0,0.65)',
                        // Long beats step down a size instead of eating the
                        // image's height (writer caps at 300 chars; this
                        // catches the top of that range).
                        fontSize: e.beat.length > 200 ? 'clamp(12px, 2.2vmin, 15px)' : 'clamp(13px, 2.6vmin, 17px)' }}>
                        {e.beat}
                      </div>
                    )}
                    {e.image_url && /^https:\/\/[a-z0-9.-]+\.amazonaws\.com\//i.test(e.image_url) ? (
                      <img key={`img-${replay.idx}`} src={e.image_url} alt="Scene from the expedition" style={{ maxWidth: '92%', maxHeight: '58%', objectFit: 'contain', borderRadius: 12, border: '2px solid rgba(255,215,110,0.45)', boxShadow: '0 10px 40px rgba(0,0,0,0.55)' }} />
                    ) : (
                      <div style={{ fontSize: 'clamp(46px, 11vmin, 96px)', lineHeight: 1 }}>{KIND[e.kind] || '📝'}</div>
                    )}
                    <div key={`cap-${replay.idx}`} style={{ maxWidth: 'min(92%, 640px)', background: 'linear-gradient(180deg, #fffdf5, #f7f0dd)', color: '#3a2f4d', border: '1.5px solid rgba(91,58,142,0.45)', borderRadius: 10, padding: 'clamp(8px, 2vmin, 14px) clamp(12px, 2.5vmin, 18px)', boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                      // Long notes were shoving the image out of frame on
                      // phones (Nina 07-24) — the card caps and scrolls
                      // instead. z4 = above the tap zones so scroll works.
                      position: 'relative', zIndex: 4, maxHeight: '46%', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                      <div style={{ fontSize: 'clamp(10px, 1.9vmin, 13px)', fontWeight: 800, color: '#8a7ba3', textTransform: 'lowercase', marginBottom: 3 }}>
                        {KIND[e.kind] || '📝'} {e.kind || 'note'}{e.feeling ? ` · feeling ${e.feeling}` : ''}{e.ts ? ` · ${new Date(e.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
                      </div>
                      <div style={{ fontSize: 'clamp(13px, 2.7vmin, 18px)', lineHeight: 1.45 }}>{e.entry}</div>
                    </div>
                  </>
                )}
              </div>
              {/* Scrubber: visible 8px bars on a 24px tap band (padding +
                  content-box clip, same trick as the explore rail dots) +
                  home-indicator clearance on notched phones. */}
              <div style={{ display: 'flex', gap: 4, paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
                {replay.entries.map((_, i) => (
                  <button key={i} type="button" onClick={() => setReplay((r) => (r ? { ...r, idx: i, playing: false } : r))} aria-label={`Note ${i + 1} of ${total}`} style={{ flex: 1, height: 24, borderRadius: 4, border: 'none', cursor: 'pointer', padding: '8px 0', boxSizing: 'border-box', backgroundClip: 'content-box', backgroundColor: i <= replay.idx ? '#ffd76e' : 'rgba(255,255,255,0.18)' }} />
                ))}
              </div>
            </div>
          );
        })()}
      </div>
      </div>
      {/* Pre-show side panel — lobby chat + field notes while the curtains
          are up. Unmounts at handoff: the real viewer brings its own panel
          (whose chat hits the same 24/7 lobby room).
          REPLAY on phones (Nina 07-24): the panel kept its half of the
          screen and squeezed the replay into the rest — portrait gave it
          50% height, landscape 340px width. Replay is a lean-back full
          attention mode; the panel yields until ✕ ends the replay. Desktop
          keeps both. */}
      {!stageHidden && !((narrow || shortViewport) && replay && !replay.empty) && (
        <TheaterSidePanel narrow={narrow} musicOn={musicOn} onToggleMusic={toggleMusic} />
      )}
    </div>
  );
}

export default ExplorerTheaterPage;
